/* eslint-disable */
import React, { useState, useRef } from "react";
import { Flowchart } from "../../shared/Flowchart";
import { Row, Box, MockBtn, MockField, MockBadge, WF, Section, CardBlock, FormBlock, DataBlock, OverlayBlock, TabBlock } from "../../shared/WireframePrimitives";
import { InspectProvider, InspectorPanel, Inspect, useInspector, InspectorToolbar } from "../../shared/Inspector";
import { canvasHeightForViewport } from "../../shared/layoutTokens";
import ViewportFrame from "../../shared/ViewportFrame";
import WizardChrome from "../../shared/WizardChrome";
import { WIZARD_TABS } from "../../shared/wizardTabs";

const T = {
  brand:"#25D366",brandHover:"#20bd5a",brandSoft:"rgba(37,211,102,0.12)",brandBorder:"rgba(37,211,102,0.28)",
  whatsapp:"#25D366",whatsappSoft:"rgba(37,211,102,0.1)",
  cyan:"#06b6d4",cyanSoft:"rgba(6,182,212,0.1)",teal:"#14b8a6",tealSoft:"rgba(20,184,166,0.1)",
  amber:"#f59e0b",amberSoft:"rgba(245,158,11,0.12)",coral:"#f97316",coralSoft:"rgba(249,115,22,0.1)",
  green:"#22c55e",greenSoft:"rgba(34,197,94,0.1)",red:"#ef4444",redSoft:"rgba(239,68,68,0.1)",violet:"#7c3aed",
  lCanvas:"#efeae2",lSurface:"#ffffff",lMuted:"#f0f2f5",lBorder:"rgba(0,0,0,0.08)",lBorderMd:"rgba(0,0,0,0.14)",
  lText:"#111b21",lTextSec:"#667781",lTextMuted:"#8696a0",
  dCanvas:"#111b21",dSurface:"#1f2c33",dElevated:"#2a3942",dMuted:"#182229",
  dBorder:"rgba(255,255,255,0.08)",dBorderMd:"rgba(255,255,255,0.14)",
  dText:"#e9edef",dTextSec:"#8696a0",dTextMuted:"#667781",
  fast:"150ms",mid:"220ms",slow:"320ms",
  ease:"cubic-bezier(0.4,0,0.2,1)",spring:"cubic-bezier(0.34,1.56,0.64,1)",
  rsm:"6px",rmd:"10px",rlg:"16px",rxl:"24px",
};
function useTheme(dark){
  return {
    bg:dark?T.dCanvas:T.lCanvas,surface:dark?T.dSurface:T.lSurface,
    elevated:dark?T.dElevated:T.lSurface,muted:dark?T.dMuted:T.lMuted,
    border:dark?T.dBorder:T.lBorder,borderMd:dark?T.dBorderMd:T.lBorderMd,
    text:dark?T.dText:T.lText,textSec:dark?T.dTextSec:T.lTextSec,textMuted:dark?T.dTextMuted:T.lTextMuted,
    sidebar:dark?"#0b141a":T.lSurface,
    dark,tr:`all ${T.mid} ${T.ease}`,trSlow:`all ${T.slow} ${T.ease}`,
  };
}

// ─── PublicShell — centered card, no chrome ────────────────────────────────────
const PublicShell = ({ dark, children }) => {
  const th = useTheme(dark);
  return (
    <Inspect id="public-shell" label="PublicShell" tokens={{background:"th.surface",accent:"T.whatsapp",gradient:"T.whatsapp -> #1a8a4a",borderRadius:"T.rlg"}} css={{background:th.surface,accent:T.whatsapp,borderRadius:T.rlg}}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"center", minHeight:"100%", background:`linear-gradient(135deg,${T.whatsapp},#1a8a4a)`, padding:24 }}>
        <div style={{ width:"100%", maxWidth:400, background:th.surface, borderRadius:T.rlg, overflow:"hidden", boxShadow:"0 8px 32px rgba(0,0,0,0.15)" }}>
          <Inspect id="public-shell-header" label="Auth header" tokens={{background:"T.whatsapp"}} css={{background:T.whatsapp}}>
            <div style={{ background:T.whatsapp, padding:"18px 24px", textAlign:"center" }}>
              <div style={{ display:"inline-flex", alignItems:"center", gap:8 }}>
                <div style={{ width:32,height:32,borderRadius:"50%",background:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:14,color:T.whatsapp,fontWeight:700 }}>Z</div>
                <span style={{ color:"#fff",fontWeight:600,fontSize:18 }}>ZiBuzz</span>
              </div>
            </div>
          </Inspect>
          <div style={{ padding:"24px 28px 28px" }}>{children}</div>
        </div>
      </div>
    </Inspect>
  );
};

// ─── SetupShell — minimal chrome, step progress ────────────────────────────────
const SetupShell = ({ dark, children, step, total }) => {
  const th = useTheme(dark);
  return (
    <Inspect id="setup-shell" label="SetupShell" tokens={{background:"th.bg",surface:"th.surface",border:"th.border",accent:"T.teal"}} css={{background:th.bg,border:`1px solid ${th.border}`,accent:T.teal}}>
      <div style={{ display:"flex", flexDirection:"column", minHeight:"100%", background:th.bg }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"10px 16px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:28,height:28,borderRadius:"50%",background:T.teal,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",fontWeight:700 }}>Z</div>
            <span style={{ fontSize:13, fontWeight:600, color:th.text }}>WhatsApp Setup</span>
          </div>
          <span style={{ fontSize:10, color:th.textMuted }}>Step {step||1} of {total||3}</span>
        </div>
        <div style={{ padding:"0 16px", background:th.surface, borderBottom:`1px solid ${th.border}` }}>
          <div style={{ display:"flex", gap:4, padding:"8px 0" }}>
            {Array.from({length:total||3}, (_,i) => (
              <div key={i} style={{ flex:1, height:4, borderRadius:2, background:(step||1)>i?T.teal:th.muted, transition:T.mid }} />
            ))}
          </div>
        </div>
        <div style={{ flex:1 }}>{children}</div>
      </div>
    </Inspect>
  );
};

// ─── ZibuzzAppShell — sidebar nav/tab bar for main app screens ─────────────────
const ZibuzzAppShell = ({ dark, children, viewport, activeTab }) => {
  const th = useTheme(dark);
  const mob = viewport === "mobile";
  const tabs = [
    { key:"inbox", label:"Inbox", icon:"💬" },
    { key:"templates", label:"Templates", icon:"📋" },
    { key:"subscription", label:"Subscription", icon:"💳" },
    { key:"team", label:"Team", icon:"👥" },
    { key:"profile", label:"Profile", icon:"\u2699️" },
  ];

  if (mob) {
    return (
      <Inspect id="zibuzz-app-shell-mobile" label="ZibuzzAppShell mobile" tokens={{background:"th.bg",surface:"th.surface",border:"th.border",accent:"T.whatsapp"}} css={{background:th.bg,border:`1px solid ${th.border}`,accent:T.whatsapp}}>
        <div style={{ display:"flex", flexDirection:"column", minHeight:"100%", background:th.bg }}>
          <div style={{ flex:1, display:"flex", flexDirection:"column" }}>{children}</div>
          <div style={{ display:"flex", borderTop:`1px solid ${th.border}`, background:th.surface }}>
            {tabs.map(t => (
              <div key={t.key} style={{ flex:1, textAlign:"center", padding:"5px 0", fontSize:8, color:t.key===(activeTab||"inbox")?T.brand:th.textMuted, borderTop:t.key===(activeTab||"inbox")?`2px solid ${T.brand}`:"2px solid transparent", cursor:"default" }}>
                <div style={{ fontSize:14,marginBottom:1 }}>{t.icon}</div>
                {t.label}
              </div>
            ))}
          </div>
        </div>
      </Inspect>
    );
  }

  return (
    <Inspect id="zibuzz-app-shell-desktop" label="ZibuzzAppShell desktop" tokens={{background:"th.bg",surface:"th.surface",sidebar:"th.sidebar",border:"th.border",accent:"T.whatsapp"}} css={{background:th.bg,border:`1px solid ${th.border}`,accent:T.whatsapp}}>
      <div style={{ display:"flex", flexDirection:"column", minHeight:"100%", background:th.bg }}>
        <Inspect id="zibuzz-topbar" label="Topbar" tokens={{background:"th.surface",border:"th.border"}} css={{background:th.surface,border:`1px solid ${th.border}`}}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 16px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              <div style={{ width:28,height:28,borderRadius:"50%",background:T.whatsapp,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",fontWeight:700 }}>Z</div>
              <div style={{ fontSize:15,fontWeight:600,color:th.text }}>ZiBuzz</div>
            </div>
            <div style={{ width:28,height:28,borderRadius:"50%",background:T.brandSoft,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:T.brand,cursor:"default" }}>U</div>
          </div>
        </Inspect>
        <div style={{ flex:1, display:"flex" }}>
          <Inspect id="zibuzz-sidebar" label="Sidebar nav" tokens={{background:"th.surface",border:"th.border",text:"th.textSec",accent:"T.whatsapp"}} css={{background:th.surface,border:`1px solid ${th.border}`,accent:T.whatsapp}}>
            <div style={{ width:170, borderRight:`1px solid ${th.border}`, background:th.surface, display:"flex", flexDirection:"column", padding:"4px 0", flexShrink:0 }}>
              {tabs.map(t => (
                <div key={t.key} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 14px", cursor:"default", background:t.key===(activeTab||"inbox")?T.brandSoft:"transparent", color:t.key===(activeTab||"inbox")?T.brand:th.textSec, fontSize:11, fontWeight:t.key===(activeTab||"inbox")?500:400, borderRight:t.key===(activeTab||"inbox")?`2px solid ${T.brand}`:"2px solid transparent" }}>
                  <span style={{ fontSize:14 }}>{t.icon}</span>
                  <span>{t.label}</span>
                </div>
              ))}
            </div>
          </Inspect>
          <div style={{ flex:1, display:"flex", flexDirection:"column" }}>{children}</div>
        </div>
      </div>
    </Inspect>
  );
};

// ─── ROUTES ────────────────────────────────────────────────────────────────────
const ROUTES = [
  { path:"/login", comp:"Login", shell:"PublicShell", auth:"public", app:"zibuzz", note:"Entry point. Redirects to /inbox if already authenticated." },
  { path:"/register", comp:"Register", shell:"PublicShell", auth:"public", app:"zibuzz", note:"Multi-step: credentials + OTP verification + success redirect." },
  { path:"/reset-password", comp:"ResetPassword", shell:"PublicShell", auth:"public", app:"zibuzz", note:"3-step: request OTP -> verify -> set new password -> success." },
  { path:"/whatsapp-setup", comp:"WhatsAppSetup", shell:"SetupShell", auth:"protected", app:"zibuzz", note:"Wizard: entry -> OAuth -> callback -> number select -> finalise -> ready." },
  { path:"/", comp:"InboxShell", shell:"ZibuzzAppShell", auth:"protected", app:"zibuzz", note:"Web: split-pane with inbox sidebar + chat pane. Mobile: navigates to inbox tab." },
  { path:"/chat/:contactId", comp:"ChatTimeline", shell:"ZibuzzAppShell", auth:"protected", app:"zibuzz", note:"Dynamic chat route. Web: renders in right pane. Mobile: stacked full-screen route." },
  { path:"/templates", comp:"TemplateManager", shell:"ZibuzzAppShell", auth:"protected", app:"zibuzz", note:"Gated: requires wabaId + active subscription. Tab shell: List, Editor, Library." },
  { path:"/subscription", comp:"Subscription", shell:"ZibuzzAppShell", auth:"protected", app:"zibuzz", note:"Accessible to all authenticated users including inactive. Entitlement-aware header." },
  { path:"/team", comp:"TeamManagement", shell:"ZibuzzAppShell", auth:"protected", app:"zibuzz", note:"Role-aware: admin sees full controls; team_member sees resign path only." },
  { path:"/profile", comp:"Profile", shell:"ZibuzzAppShell", auth:"protected", app:"zibuzz", note:"Profile info + quick actions with gating states + security section." },
];

// ─── SHELLS ────────────────────────────────────────────────────────────────────
const SHELLS = [
  { id:"public-shell", label:"PublicShell", who:"Visitor", topbar:"None", sidebar:"None", note:"Login, Register, ResetPassword use no app shell. Centered card on brand gradient." },
  { id:"setup-shell", label:"SetupShell", who:"Merchant (onboarding)", topbar:"ZiBuzz logo + step indicator", sidebar:"None", note:"WhatsApp activation wizard. Minimal chrome with step progress bar. WhatsApp-green variant." },
  { id:"zibuzz-app-shell", label:"ZibuzzAppShell", who:"Merchant", topbar:"ZiBuzz logo + user avatar", sidebar:"5-tab sidebar (Inbox, Templates, Subscription, Team, Profile). Mobile: bottom tab bar.", note:"WhatsApp-green brand accent. Inbox-first UX with split-pane on web." },
];

// ─── IA DATA ───────────────────────────────────────────────────────────────────
const IA_GROUPS = [
  {
    id:"access", label:"Access", icon:"🔐",
    screens:[
      { id:"login", label:"Login", tag:"core" },
      { id:"login-error", label:"Login - Invalid Credentials", tag:"state" },
      { id:"register", label:"Register - Credentials", tag:"form" },
      { id:"register-otp", label:"Register - OTP Verification", tag:"form" },
      { id:"register-success", label:"Register - Success Redirect", tag:"state" },
      { id:"reset-request", label:"Reset Password - Request", tag:"form" },
      { id:"reset-otp", label:"Reset Password - Verify OTP", tag:"form" },
      { id:"reset-new-password", label:"Reset Password - New Password", tag:"form" },
      { id:"reset-success", label:"Reset Password - Success", tag:"state" },
    ]
  },
  {
    id:"activation", label:"WhatsApp Activation", icon:"📲",
    note:"Setup wizard - minimal chrome, not in main nav",
    screens:[
      { id:"setup-entry", label:"Setup Entry", tag:"core" },
      { id:"setup-oauth", label:"Meta Embedded Signup", tag:"core" },
      { id:"setup-callback-progress", label:"OAuth Callback - Processing", tag:"state" },
      { id:"setup-number-select", label:"Number Selection", tag:"form" },
      { id:"setup-finalize", label:"Finalise Configuration", tag:"form" },
      { id:"setup-ready", label:"Readiness Confirmation", tag:"state" },
      { id:"setup-blocked", label:"Setup Blocked - Error Recovery", tag:"state" },
    ]
  },
  {
    id:"conversation", label:"Conversation", icon:"💬",
    screens:[
      { id:"inbox", label:"Inbox Thread List", tag:"core" },
      { id:"inbox-empty", label:"Inbox - Empty State", tag:"state" },
      { id:"chat-timeline", label:"Chat Timeline", tag:"core" },
      { id:"chat-send-pending", label:"Chat - Outbound Pending", tag:"state" },
      { id:"chat-send-error", label:"Chat - Send Error", tag:"state" },
      { id:"chat-composer", label:"Composer - Multi-mode", tag:"core" },
      { id:"chat-reactions", label:"Message Actions - React and Reply", tag:"modal" },
      { id:"chat-socket-banner", label:"Socket Reconnect Banner", tag:"state" },
    ]
  },
  {
    id:"templates", label:"Templates", icon:"📋",
    screens:[
      { id:"template-gated", label:"Templates - Gating Check", tag:"gated" },
      { id:"template-list", label:"Template List", tag:"core" },
      { id:"template-editor", label:"Template Editor", tag:"form" },
      { id:"template-library", label:"Template Library", tag:"preview" },
      { id:"template-send-modal", label:"Send Template Modal", tag:"modal" },
      { id:"template-send-error", label:"Send Template - Validation Error", tag:"state" },
    ]
  },
  {
    id:"subscription", label:"Subscription", icon:"💳",
    screens:[
      { id:"subs-overview", label:"Subscription Overview", tag:"core" },
      { id:"subs-plan-cards", label:"Plan Cards", tag:"core" },
      { id:"subs-trial", label:"Trial CTA - Solo Eligible", tag:"gated" },
      { id:"subs-cta-states", label:"CTA State Matrix", tag:"state" },
      { id:"subs-checkout", label:"Checkout", tag:"core" },
      { id:"subs-verify", label:"Verify Payment", tag:"state" },
      { id:"subs-payment-error", label:"Payment Error State", tag:"state" },
    ]
  },
  {
    id:"administration", label:"Administration", icon:"\u2699️",
    screens:[
      { id:"team-admin", label:"Team - Admin View", tag:"core" },
      { id:"team-member", label:"Team - Member View", tag:"core" },
      { id:"team-add-form", label:"Add Member Form", tag:"modal" },
      { id:"team-add-error", label:"Add Member - Validation Error", tag:"state" },
      { id:"profile-overview", label:"Profile Overview", tag:"core" },
      { id:"profile-gating", label:"Profile - Gating Message", tag:"gated" },
      { id:"profile-password", label:"Change Password", tag:"form" },
      { id:"profile-logout", label:"Logout Confirm", tag:"modal" },
    ]
  },
];

// ─── FLOWS ─────────────────────────────────────────────────────────────────────
const FLOWS = [
  {
    id:"zibuzz-access", label:"Access and Session", who:"merchant", color:"T.brand",
    steps:[
      { id:"login", label:"Login", shell:"PublicShell", note:"Centered card with country-code dropdown + mobile field + password field. Primary CTA 'Log In' full-width. Secondary links: 'Register' and 'Forgot Password'. Inline field errors below each input; global error banner above form on failed auth." },
      { id:"login-error", label:"Login - Invalid Credentials", shell:"PublicShell", note:"Full login form preserved. Alert banner (type=error) above form: 'Incorrect mobile or password'. Fields retain entered values. No form reset." },
      { id:"register", label:"Register - Credentials", shell:"PublicShell", note:"Step 1 of 2. Name, country-code dropdown + mobile, email, password + confirm password. 'Create Account' primary CTA. Real-time strength indicator on password field. Error inline per field." },
      { id:"register-otp", label:"Register - OTP Verification", shell:"PublicShell", note:"Step 2 of 2. Full step-1 form still visible above OTP box. 6 square inputs, resend timer, error state with attempts-remaining." },
      { id:"register-success", label:"Register - Success Redirect", shell:"PublicShell", note:"Verified checkmark animation. 'Account created' message. Auto-redirects to WhatsApp setup entry if wabaId absent, else to inbox." },
      { id:"reset-request", label:"Reset Password - Request", shell:"PublicShell", note:"Single field: country-code dropdown + mobile. 'Send OTP' primary CTA. Back-to-login secondary link." },
      { id:"reset-otp", label:"Reset Password - Verify OTP", shell:"PublicShell", note:"Full reset-request form visible above OTP box. States: cooldown, resend-enabled, wrong-otp, otp-expired, lockout." },
      { id:"reset-new-password", label:"Reset Password - New Password", shell:"PublicShell", note:"New password + confirm password fields. Strength rule indicator. 'Set Password' CTA disabled until fields valid and match." },
      { id:"reset-success", label:"Reset Password - Success", shell:"PublicShell", note:"Success banner: 'Password updated'. Single CTA: 'Back to Login'. No auto-redirect." },
    ]
  },
  {
    id:"zibuzz-activation", label:"WhatsApp Activation", who:"merchant", color:"T.teal",
    steps:[
      { id:"setup-entry", label:"Setup Entry", shell:"SetupShell", note:"Status card showing connection state. Single primary CTA. Progress bar: step 1 of 3." },
      { id:"setup-oauth", label:"Meta Embedded Signup", shell:"SetupShell", note:"Step 2 of 3. Loading indicator while Meta SDK initialises. Cancel affordance visible." },
      { id:"setup-callback-progress", label:"OAuth Callback - Processing", shell:"SetupShell", note:"Spinner + 'Verifying your account' message. Polls /whatsappWaDiscover." },
      { id:"setup-number-select", label:"Number Selection", shell:"SetupShell", note:"Step 2.5. Table of available phone numbers. Radio selection. 'Confirm Number' CTA." },
      { id:"setup-finalize", label:"Finalise Configuration", shell:"SetupShell", note:"Step 3 of 3. Summary card. 'Activate' CTA triggers /whatsappWaFinalize." },
      { id:"setup-ready", label:"Readiness Confirmation", shell:"SetupShell", note:"Checklist of unlocked features. 'Go to Inbox' CTA." },
      { id:"setup-blocked", label:"Setup Blocked - Error Recovery", shell:"SetupShell", note:"Specific error text. 'Try Again' + 'Contact Support' CTAs." },
    ]
  },
  {
    id:"zibuzz-conversation", label:"Conversation - Inbox to Chat", who:"merchant", color:"T.cyan",
    steps:[
      { id:"inbox", label:"Inbox Thread List", shell:"ZibuzzAppShell", note:"Left pane (web) or full screen (mobile). Thread cards with unread, recency sort." },
      { id:"inbox-empty", label:"Inbox - Empty State", shell:"ZibuzzAppShell", note:"Illustration + 'No conversations yet'. CTA to send first message or setup WhatsApp." },
      { id:"chat-timeline", label:"Chat Timeline", shell:"ZibuzzAppShell", note:"Chronological bubbles. Delivery status icons. Skeleton loading rows." },
      { id:"chat-send-pending", label:"Chat - Outbound Pending", shell:"ZibuzzAppShell", note:"Optimistic bubble with clock icon. Transitions on socket ack." },
      { id:"chat-send-error", label:"Chat - Send Error", shell:"ZibuzzAppShell", note:"Inline error strip below the failed bubble. Retry affordance." },
      { id:"chat-composer", label:"Composer - Multi-mode", shell:"ZibuzzAppShell", note:"Auto-grow text input. Attach/template/link/location icons." },
      { id:"chat-reactions", label:"Message Actions - React and Reply", shell:"ZibuzzAppShell", note:"Long-press or hover menu: React, Reply, Copy. Emoji-grid overlay." },
      { id:"chat-socket-banner", label:"Socket Reconnect Banner", shell:"ZibuzzAppShell", note:"Amber status strip at top of chat. Auto-hides on restore." },
    ]
  },
  {
    id:"zibuzz-templates", label:"Template Lifecycle", who:"merchant", color:"T.violet",
    steps:[
      { id:"template-gated", label:"Templates - Gating Check", shell:"ZibuzzAppShell", note:"Check wabaId + active subscription. Gating card with CTA." },
      { id:"template-list", label:"Template List", shell:"ZibuzzAppShell", note:"Filterable, sortable table with approval status badges." },
      { id:"template-editor", label:"Template Editor", shell:"ZibuzzAppShell", note:"Form with placeholder tokens, live preview panel, character count." },
      { id:"template-library", label:"Template Library", shell:"ZibuzzAppShell", note:"Pre-built templates browsable by category. Preview + import." },
      { id:"template-send-modal", label:"Send Template Modal", shell:"ZibuzzAppShell", note:"Recipient selector -> placeholder fill -> send." },
      { id:"template-send-error", label:"Send Template - Validation Error", shell:"ZibuzzAppShell", note:"Inline error per unfilled placeholder. Send disabled until valid." },
    ]
  },
  {
    id:"zibuzz-subscription", label:"Subscription and Growth", who:"merchant", color:"T.amber",
    steps:[
      { id:"subs-overview", label:"Subscription Overview", shell:"ZibuzzAppShell", note:"Entitlement-aware header. Billing cycle segmented control." },
      { id:"subs-plan-cards", label:"Plan Cards", shell:"ZibuzzAppShell", note:"Solo/Galaxy cards with live price, savings badge, feature list." },
      { id:"subs-trial", label:"Trial CTA - Solo Eligible", shell:"ZibuzzAppShell", note:"'Activate 7-Day Trial' on Solo card. Conditional visibility." },
      { id:"subs-cta-states", label:"CTA State Matrix", shell:"ZibuzzAppShell", note:"Login to Subscribe / Renew Plan / Get Started per condition." },
      { id:"subs-checkout", label:"Checkout - Native and Web", shell:"ZibuzzAppShell", note:"Cashfree deeplink or embedded iframe. Pending checkout card." },
      { id:"subs-verify", label:"Verify Payment", shell:"ZibuzzAppShell", note:"Pending card with Verify CTA. Success/failure handling." },
      { id:"subs-payment-error", label:"Payment Error State", shell:"ZibuzzAppShell", note:"Specific error message, retry + support CTAs." },
    ]
  },
  {
    id:"zibuzz-administration", label:"Team and Profile Administration", who:"merchant", color:"T.coral",
    steps:[
      { id:"team-admin", label:"Team - Admin View", shell:"ZibuzzAppShell", note:"Member list with add/remove. Resigned subline with date." },
      { id:"team-member", label:"Team - Member View", shell:"ZibuzzAppShell", note:"Same list. Only resign action visible. No admin affordances." },
      { id:"team-add-form", label:"Add Member Form", shell:"ZibuzzAppShell", note:"Country code + mobile input. Live validation. 'Add' CTA." },
      { id:"team-add-error", label:"Add Member - Validation Error", shell:"ZibuzzAppShell", note:"Inline error under mobile field. Form preserved." },
      { id:"profile-overview", label:"Profile Overview", shell:"ZibuzzAppShell", note:"Avatar, info, quick actions with gating states." },
      { id:"profile-gating", label:"Profile - Gating Message", shell:"ZibuzzAppShell", note:"Gate-specific copy with direct CTA to required screen." },
      { id:"profile-password", label:"Change Password", shell:"ZibuzzAppShell", note:"Current + new + confirm. Strength rule. Success banner." },
      { id:"profile-logout", label:"Logout Confirm", shell:"ZibuzzAppShell", note:"Confirmation dialog. Destructive red CTA." },
    ]
  },
];

// ─── TECH STACK ─────────────────────────────────────────────────────────────────
const TECH_STACK = [
  ["Mobile","React Native + Expo + TypeScript + NativeWind + Tamagui"],
  ["Web","React 19 + Ant Design 6 + Axios"],
  ["Backend","AWS SAM + Node 22 Lambda + DynamoDB + WebSocket API"],
  ["Database","DynamoDB (Msg, Thread, Connection tables)"],
  ["Realtime","WebSocket API + Socket.IO client"],
  ["Messaging","Meta WhatsApp Cloud API (Graph API v22)"],
  ["Payments","Cashfree (WebView on mobile, embedded on web)"],
  ["Auth","JWT + bcrypt + Phone OTP"],
  ["Infra","API Gateway + S3 + CloudFront + Lambda@Edge"],
  ["WhatsApp Setup","Meta Embedded Signup OAuth + WABA Discovery"],
  ["Cost","~$30-50/mo at current scale (Lambda + DynamoDB + API Gateway)"],
];

// ─── CONFIGS ────────────────────────────────────────────────────────────────────
const CONFIGS = [
  { name:"Solo", label:"Solo (₹499/mo)", customers:"500", msgPerDay:"200", teamMembers:"1", automation:false, broadcast:false, analytics:false },
  { name:"Galaxy", label:"Galaxy (₹1,999/mo)", customers:"Unlimited", msgPerDay:"Unlimited", teamMembers:"10", automation:true, broadcast:true, analytics:true },
];

// ─── STEP TO SCREEN MAP ─────────────────────────────────────────────────────────
const STEP_TO_SCREEN = {
  "login":"login","login-error":"login-error","register":"register","register-otp":"register-otp","register-success":"register-success",
  "reset-request":"reset-request","reset-otp":"reset-otp","reset-new-password":"reset-new-password","reset-success":"reset-success",
  "setup-entry":"setup-entry","setup-oauth":"setup-oauth","setup-callback-progress":"setup-callback-progress",
  "setup-number-select":"setup-number-select","setup-finalize":"setup-finalize","setup-ready":"setup-ready","setup-blocked":"setup-blocked",
  "inbox":"inbox","inbox-empty":"inbox-empty","chat-timeline":"chat-timeline","chat-send-pending":"chat-send-pending",
  "chat-send-error":"chat-send-error","chat-composer":"chat-composer","chat-reactions":"chat-reactions","chat-socket-banner":"chat-socket-banner",
  "template-gated":"template-gated","template-list":"template-list","template-editor":"template-editor",
  "template-library":"template-library","template-send-modal":"template-send-modal","template-send-error":"template-send-error",
  "subs-overview":"subs-overview","subs-plan-cards":"subs-plan-cards","subs-trial":"subs-trial","subs-cta-states":"subs-cta-states",
  "subs-checkout":"subs-checkout","subs-verify":"subs-verify","subs-payment-error":"subs-payment-error",
  "team-admin":"team-admin","team-member":"team-member","team-add-form":"team-add-form","team-add-error":"team-add-error",
  "profile-overview":"profile-overview","profile-gating":"profile-gating","profile-password":"profile-password","profile-logout":"profile-logout",
};

// ─── SCREEN WIREFRAMES ─────────────────────────────────────────────────────────
function ScreenWF({ dark, viewport, screen, slim }) {
  const th = useTheme(dark);
  const mob = viewport === "mobile";

  const screens = {
    // ── F1: ACCESS ──────────────────────────────────────────────────────────
    "login": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ fontSize:15, fontWeight:600, color:th.text, textAlign:"center", marginBottom:20 }}>Sign in to ZiBuzz</div>
          <div style={{ marginBottom:10 }}>
            <MockField label="Country Code" val="+91 🇮🇳" req />
          </div>
          <div style={{ marginBottom:10 }}>
            <MockField label="Mobile Number" val="9876543210" req />
          </div>
          <div style={{ marginBottom:14 }}>
            <MockField label="Password" val="••••••••" req />
          </div>
          <div style={{ marginBottom:8 }}>
            <MockBtn label="Log In" type="primary" style={{width:"100%"}} />
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.brand }}>
            <span style={{ cursor:"default" }}>Register</span>
            <span style={{ cursor:"default" }}>Forgot Password?</span>
          </div>
        </PublicShell>
      </WF>
    ),
    "login-error": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ padding:"8px 12px", background:T.redSoft, border:`1px solid ${T.red}`, borderRadius:T.rsm, marginBottom:12, fontSize:10, color:T.red, fontWeight:500 }}>Incorrect mobile or password. Please try again.</div>
          <div style={{ fontSize:15, fontWeight:600, color:th.text, textAlign:"center", marginBottom:20 }}>Sign in to ZiBuzz</div>
          <div style={{ marginBottom:10 }}>
            <MockField label="Country Code" val="+91 🇮🇳" req />
          </div>
          <div style={{ marginBottom:10 }}>
            <MockField label="Mobile Number" val="9876543210" req />
          </div>
          <div style={{ marginBottom:14 }}>
            <MockField label="Password" val="••••••••" req />
          </div>
          <div style={{ marginBottom:8 }}>
            <MockBtn label="Log In" type="primary" style={{width:"100%"}} />
          </div>
          <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:T.brand }}>
            <span style={{ cursor:"default" }}>Register</span>
            <span style={{ cursor:"default" }}>Forgot Password?</span>
          </div>
        </PublicShell>
      </WF>
    ),
    "register": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ fontSize:14, fontWeight:600, color:th.text, textAlign:"center", marginBottom:4 }}>Create Your Account</div>
          <div style={{ fontSize:9, color:th.textMuted, textAlign:"center", marginBottom:14 }}>Step 1 of 2 — Account Details</div>
          <div style={{ marginBottom:8 }}>
            <MockField label="Full Name" val="Ravi Sharma" req />
          </div>
          <div style={{ marginBottom:8 }}>
            <MockField label="Country Code" val="+91 🇮🇳" req />
          </div>
          <div style={{ marginBottom:8 }}>
            <MockField label="Mobile Number" val="9876543210" req />
          </div>
          <div style={{ marginBottom:8 }}>
            <MockField label="Email" val="ravi@example.com" req />
          </div>
          <div style={{ marginBottom:6 }}>
            <MockField label="Password" val="••••••••" req />
          </div>
          <div style={{ height:3, width:"60%", background:T.green, borderRadius:2, marginBottom:6 }} />
          <div style={{ marginBottom:10 }}>
            <MockField label="Confirm Password" val="••••••••" req />
          </div>
          <div style={{ marginBottom:8 }}>
            <MockBtn label="Create Account" type="primary" style={{width:"100%"}} />
          </div>
          <div style={{ fontSize:9, color:th.textMuted, textAlign:"center" }}>
            Already have an account? <span style={{ color:T.brand, cursor:"default" }}>Log In</span>
          </div>
        </PublicShell>
      </WF>
    ),
    "register-otp": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ fontSize:14, fontWeight:600, color:th.text, textAlign:"center", marginBottom:4 }}>Verify Your Number</div>
          <div style={{ fontSize:9, color:th.textMuted, textAlign:"center", marginBottom:14 }}>Step 2 of 2 — OTP sent to WhatsApp</div>
          <div style={{ padding:"8px 10px", background:th.muted, borderRadius:T.rsm, marginBottom:12, fontSize:9, color:th.textSec }}>
            <div>Full Name: <strong>Ravi Sharma</strong></div>
            <div>Mobile: <strong>+91-9876543210</strong></div>
            <div>Email: <strong>ravi@example.com</strong></div>
          </div>
          <div style={{ background:T.cyanSoft, borderRadius:T.rmd, padding:"14px 16px", marginBottom:12 }}>
            <div style={{ fontSize:10, color:th.text, textAlign:"center", marginBottom:10 }}>OTP sent to WhatsApp</div>
            <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:8 }}>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style={{ width:28,height:28,borderRadius:5,border:`2px solid ${T.cyan}`,background:dark?"rgba(255,255,255,0.15)":"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:th.text }}>{i<=2?String(i):""}</div>
              ))}
            </div>
            <div style={{ fontSize:9, color:th.textMuted, textAlign:"center" }}>Resend in <strong style={{ color:T.brand }}>0:35</strong></div>
            <div style={{ fontSize:9, color:T.brand, textAlign:"center", cursor:"default", marginTop:4 }}>Change number</div>
          </div>
          <MockBtn label="Verify OTP" type="primary" style={{width:"100%"}} />
        </PublicShell>
      </WF>
    ),
    "register-success": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ width:48,height:48,borderRadius:"50%",background:T.green,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:22,color:"#fff" }}>✓</div>
            <div style={{ fontSize:16, fontWeight:600, color:th.text, marginBottom:6 }}>Account Created!</div>
            <div style={{ fontSize:10, color:th.textSec, marginBottom:20 }}>Redirecting to your inbox…</div>
            <MockBtn label="Continue" type="primary" style={{width:"100%"}} />
          </div>
        </PublicShell>
      </WF>
    ),
    "reset-request": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ fontSize:14, fontWeight:600, color:th.text, textAlign:"center", marginBottom:4 }}>Reset Password</div>
          <div style={{ fontSize:9, color:th.textMuted, textAlign:"center", marginBottom:14 }}>Enter your mobile to receive an OTP</div>
          <div style={{ marginBottom:10 }}>
            <MockField label="Country Code" val="+91 🇮🇳" req />
          </div>
          <div style={{ marginBottom:14 }}>
            <MockField label="Mobile Number" val="" req />
          </div>
          <MockBtn label="Send OTP" type="primary" style={{width:"100%"}} />
          <div style={{ fontSize:9, color:T.brand, textAlign:"center", marginTop:10, cursor:"default" }}>Back to Login</div>
        </PublicShell>
      </WF>
    ),
    "reset-otp": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ fontSize:14, fontWeight:600, color:th.text, textAlign:"center", marginBottom:4 }}>Verify OTP</div>
          <div style={{ fontSize:9, color:th.textMuted, textAlign:"center", marginBottom:14 }}>Enter the code sent to +91-98765*****</div>
          <div style={{ background:T.cyanSoft, borderRadius:T.rmd, padding:"14px 16px", marginBottom:12 }}>
            <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:8 }}>
              {[1,2,3,4,5,6].map(i => (
                <div key={i} style={{ width:28,height:28,borderRadius:5,border:`2px solid ${T.cyan}`,background:dark?"rgba(255,255,255,0.15)":"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:th.text }}>{i<=3?String(i):""}</div>
              ))}
            </div>
            <div style={{ fontSize:9, color:th.textMuted, textAlign:"center" }}>Resend in <strong style={{ color:T.brand }}>0:25</strong></div>
          </div>
          <MockBtn label="Verify OTP" type="primary" style={{width:"100%"}} />
          <div style={{ fontSize:9, color:T.brand, textAlign:"center", marginTop:10, cursor:"default" }}>Back to Login</div>
        </PublicShell>
      </WF>
    ),
    "reset-new-password": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ fontSize:14, fontWeight:600, color:th.text, textAlign:"center", marginBottom:4 }}>Set New Password</div>
          <div style={{ fontSize:9, color:th.textMuted, textAlign:"center", marginBottom:14 }}>Must be at least 8 characters</div>
          <div style={{ marginBottom:6 }}>
            <MockField label="New Password" val="••••••••" req />
          </div>
          <div style={{ height:3, width:"80%", background:T.green, borderRadius:2, marginBottom:10 }} />
          <div style={{ marginBottom:12 }}>
            <MockField label="Confirm New Password" val="••••••••" req />
          </div>
          <MockBtn label="Set Password" type="primary" style={{width:"100%"}} />
        </PublicShell>
      </WF>
    ),
    "reset-success": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <PublicShell dark={dark}>
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ width:48,height:48,borderRadius:"50%",background:T.green,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:22,color:"#fff" }}>✓</div>
            <div style={{ fontSize:16, fontWeight:600, color:th.text, marginBottom:6 }}>Password Updated</div>
            <div style={{ fontSize:10, color:th.textSec, marginBottom:20 }}>Your password has been reset successfully.</div>
            <MockBtn label="Back to Login" type="primary" style={{width:"100%"}} />
          </div>
        </PublicShell>
      </WF>
    ),
    // ── F2: ACTIVATION ───────────────────────────────────────────────────────────
    "setup-entry": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <SetupShell dark={dark} step={1} total={3}>
          <div style={{ padding:"20px 18px" }}>
            <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16, marginBottom:16 }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 }}>
                <span style={{ fontSize:12, fontWeight:600, color:th.text }}>WhatsApp Connection</span>
                <MockBadge label="Not Connected" type="pending" />
              </div>
              <div style={{ fontSize:10, color:th.textSec, marginBottom:12, lineHeight:1.5 }}>
                Connect your WhatsApp Business Account to start messaging customers.
              </div>
              <MockBtn label="Connect WhatsApp" type="primary" style={{width:"100%"}} />
              <div style={{ fontSize:9, color:th.textMuted, textAlign:"center", marginTop:8, cursor:"default" }}>Skip for now</div>
            </div>
            <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:14 }}>
              <div style={{ fontSize:10, color:th.textSec, marginBottom:8 }}>After connecting you can:</div>
              {["Send and receive messages","Use message templates","Manage customer conversations"].map((item,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:6, marginBottom:4, fontSize:9, color:th.textMuted }}>
                  <span style={{ color:T.teal }}>+</span> {item}
                </div>
              ))}
            </div>
          </div>
        </SetupShell>
      </WF>
    ),
    "setup-oauth": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <SetupShell dark={dark} step={2} total={3}>
          <div style={{ padding:"30px 18px", textAlign:"center" }}>
            <div style={{ fontSize:24, marginBottom:12 }}>🔗</div>
            <div style={{ fontSize:12, color:th.text, fontWeight:500, marginBottom:6 }}>Opening WhatsApp Business Account Setup</div>
            <div style={{ fontSize:9, color:th.textMuted, marginBottom:20 }}>You will be redirected to Meta to complete the signup.</div>
            <div style={{ width:32,height:32,border:`3px solid ${T.teal}`,borderTop:`3px solid transparent`,borderRadius:"50%",margin:"0 auto",animation:"spin 0.8s linear infinite" }} />
            <div style={{ fontSize:9, color:T.brand, marginTop:16, cursor:"default" }}>Cancel</div>
          </div>
        </SetupShell>
      </WF>
    ),
    "setup-callback-progress": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <SetupShell dark={dark} step={2} total={3}>
          <div style={{ padding:"30px 18px", textAlign:"center" }}>
            <div style={{ width:40,height:40,border:`3px solid ${th.border}`,borderTop:`3px solid ${T.teal}`,borderRadius:"50%",margin:"0 auto 14px",animation:"spin 0.8s linear infinite" }} />
            <div style={{ fontSize:12, color:th.text, fontWeight:500, marginBottom:6 }}>Verifying your account…</div>
            <div style={{ fontSize:9, color:th.textMuted }}>Please wait while we set things up.</div>
          </div>
        </SetupShell>
      </WF>
    ),
    "setup-number-select": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <SetupShell dark={dark} step={3} total={3}>
          <div style={{ padding:"16px 18px" }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:4 }}>Select your WhatsApp number</div>
            <div style={{ fontSize:9, color:th.textMuted, marginBottom:14 }}>Choose which number to connect with ZiBuzz</div>
            {[
              {num:"+91 98765 43210", name:"Ravi's Business", status:"Active"},
              {num:"+91 87654 32109", name:"Ravi's Store", status:"Pending"},
            ].map((n,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", border:`1px solid ${i===0?T.teal:th.border}`, borderRadius:T.rsm, marginBottom:8, background:i===0?T.tealSoft:th.surface, cursor:"default" }}>
                <div style={{ width:18,height:18,borderRadius:"50%",border:`2px solid ${i===0?T.teal:th.borderMd}`,display:"flex",alignItems:"center",justifyContent:"center" }}>
                  {i===0 && <div style={{ width:10,height:10,borderRadius:"50%",background:T.teal }} />}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:11, fontWeight:500, color:th.text }}>{n.num}</div>
                  <div style={{ fontSize:9, color:th.textSec }}>{n.name}</div>
                </div>
                <MockBadge label={n.status} type={i===0?"active":"pending"} />
              </div>
            ))}
            <div style={{ marginTop:8 }}>
              <MockBtn label="Confirm Number" type="primary" style={{width:"100%"}} />
            </div>
          </div>
        </SetupShell>
      </WF>
    ),
    "setup-finalize": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <SetupShell dark={dark} step={3} total={3}>
          <div style={{ padding:"16px 18px" }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Finalise Configuration</div>
            <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:14, marginBottom:14 }}>
              {[
                {label:"Selected Number", value:"+91 98765 43210"},
                {label:"WABA Name", value:"Ravi's Business"},
                {label:"Webhook Endpoint", value:"api.zibuzz.ziniz.online/webhook"},
              ].map((row,i) => (
                <div key={i} style={{ display:"flex", justifyContent:"space-between", padding:"6px 0", borderBottom:i<2?`1px solid ${th.border}`:"none", fontSize:10 }}>
                  <span style={{ color:th.textSec }}>{row.label}</span>
                  <span style={{ color:th.text, fontWeight:500 }}>{row.value}</span>
                </div>
              ))}
            </div>
            <MockBtn label="Activate" type="primary" style={{width:"100%"}} />
          </div>
        </SetupShell>
      </WF>
    ),
    "setup-ready": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <SetupShell dark={dark} step={3} total={3}>
          <div style={{ padding:"20px 18px", textAlign:"center" }}>
            <div style={{ width:48,height:48,borderRadius:"50%",background:T.teal,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:22,color:"#fff" }}>✓</div>
            <div style={{ fontSize:14, fontWeight:600, color:th.text, marginBottom:12 }}>All Set!</div>
            <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:14, textAlign:"left", marginBottom:14 }}>
              {["WhatsApp Connected","Messaging Enabled","Templates Available"].map((item,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"5px 0", fontSize:10, color:th.text }}>
                  <span style={{ color:T.teal, fontWeight:700 }}>✓</span> {item}
                </div>
              ))}
            </div>
            <MockBtn label="Go to Inbox" type="primary" style={{width:"100%"}} />
            <div style={{ fontSize:9, color:T.brand, textAlign:"center", marginTop:8, cursor:"default" }}>Manage Templates</div>
          </div>
        </SetupShell>
      </WF>
    ),
    "setup-blocked": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <SetupShell dark={dark} step={2} total={3}>
          <div style={{ padding:"24px 18px", textAlign:"center" }}>
            <div style={{ width:44,height:44,borderRadius:"50%",background:T.redSoft,display:"flex",alignItems:"center",justifyContent:"center",margin:"0 auto 12px",fontSize:20,color:T.red }}>!</div>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:6 }}>Connection Issue</div>
            <div style={{ fontSize:10, color:th.textSec, marginBottom:16, lineHeight:1.5, padding:"0 10px" }}>
              Meta account not found. Please check your Business Manager access and try again.
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <div style={{ flex:1 }}><MockBtn label="Try Again" type="primary" style={{width:"100%"}} /></div>
            </div>
            <div style={{ fontSize:9, color:T.brand, marginTop:8, cursor:"default" }}>Contact Support</div>
          </div>
        </SetupShell>
      </WF>
    ),
    // ── F3: CONVERSATION ─────────────────────────────────────────────────────────
    "inbox": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ display:"flex", flex:1, flexDirection:mob?"column":"row", minHeight:mob?400:460 }}>
            <div style={{ width:mob?"100%":260, flexShrink:0, borderRight:mob?"none":`1px solid ${th.border}`, display:"flex", flexDirection:"column" }}>
              <div style={{ padding:"8px 12px", borderBottom:`1px solid ${th.border}` }}>
                <div style={{ padding:"6px 10px", background:th.muted, borderRadius:6, fontSize:10, color:th.textMuted }}>🔍 Search contacts...</div>
              </div>
              <div style={{ flex:1, overflow:"auto" }}>
                {[
                  {name:"Priya Sharma", preview:"Is this still available?", time:"2m", unread:2, type:"inbound"},
                  {name:"Amit Kumar", preview:"Thanks! Will check.", time:"1h", unread:0, type:"outbound"},
                  {name:"Sneha Patel", preview:"When can you deliver?", time:"3h", unread:1, type:"inbound"},
                  {name:"Rajesh Singh", preview:"Order confirmed ✓", time:"1d", unread:0, type:"outbound"},
                ].map((t,i) => (
                  <div key={i} style={{ display:"flex", gap:8, padding:"9px 12px", borderBottom:`1px solid ${th.border}`, cursor:"default", background:i===0?T.brandSoft:"transparent" }}>
                    <div style={{ width:36,height:36,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:th.textMuted }}>{t.name[0]}</div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ display:"flex", justifyContent:"space-between" }}>
                        <span style={{ fontSize:11, fontWeight:500, color:th.text }}>{t.name}</span>
                        <span style={{ fontSize:8, color:th.textMuted }}>{t.time}</span>
                      </div>
                      <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:2 }}>
                        {t.unread>0 && <span style={{ background:T.brand, color:"#fff", fontSize:8, borderRadius:8, padding:"0 5px", lineHeight:"14px", fontWeight:600 }}>{t.unread}</span>}
                        <span style={{ fontSize:9, color:th.textSec, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{t.preview}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {!mob && (
              <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", background:th.bg }}>
                <div style={{ fontSize:32, marginBottom:8 }}>💬</div>
                <div style={{ fontSize:13, fontWeight:500, color:th.text, marginBottom:4 }}>Select a conversation</div>
                <div style={{ fontSize:10, color:th.textMuted }}>Choose a thread from the left to start chatting</div>
              </div>
            )}
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "inbox-empty": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, textAlign:"center", minHeight:400 }}>
            <div style={{ width:64,height:64,borderRadius:T.rmd, background:th.muted, marginBottom:12 }} />
            <div style={{ fontSize:14, fontWeight:500, color:th.text, marginBottom:4 }}>No conversations yet</div>
            <div style={{ fontSize:10, color:th.textMuted, marginBottom:16, maxWidth:240 }}>Messages from your customers will appear here. Start by sending your first message.</div>
            <MockBtn label="Send your first message" type="primary" style={{width:"100%",maxWidth:200}} />
            <div style={{ fontSize:9, color:T.brand, marginTop:8, cursor:"default" }}>Set up WhatsApp</div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "chat-timeline": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ display:"flex", flex:1, flexDirection:mob?"column":"row", minHeight:mob?480:460 }}>
            {!mob && (
              <div style={{ width:260, flexShrink:0, borderRight:`1px solid ${th.border}`, display:"flex", flexDirection:"column" }}>
                <div style={{ padding:"8px 12px", borderBottom:`1px solid ${th.border}` }}>
                  <div style={{ padding:"6px 10px", background:th.muted, borderRadius:6, fontSize:10, color:th.textMuted }}>🔍 Search contacts...</div>
                </div>
                <div style={{ flex:1, overflow:"auto" }}>
                  {["Priya Sharma","Amit Kumar","Sneha Patel"].map((n,i) => (
                    <div key={i} style={{ display:"flex", gap:8, padding:"9px 12px", borderBottom:`1px solid ${th.border}`, cursor:"default", background:i===0?T.brandSoft:"transparent" }}>
                      <div style={{ width:36,height:36,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:th.textMuted }}>{n[0]}</div>
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ fontSize:11, fontWeight:500, color:th.text }}>{n}</div>
                        <div style={{ fontSize:9, color:th.textSec, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>Last message preview...</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ flex:1, display:"flex", flexDirection:"column" }}>
              <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
                <div style={{ width:32,height:32,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:th.textMuted }}>P</div>
                <span style={{ fontSize:11, fontWeight:600, color:th.text }}>Priya Sharma</span>
              </div>
              <div style={{ flex:1, padding:"10px 14px", overflow:"auto", display:"flex", flexDirection:"column", gap:8 }}>
                {[{text:"Hi, is this item still available?",dir:"inbound",time:"10:32 AM",status:""},{text:"Yes, it's available! Would you like to order?",dir:"outbound",time:"10:33 AM",status:"read"},{text:"Great! Please confirm the price.",dir:"inbound",time:"10:34 AM",status:""},{text:"₹499 with free shipping. Shall I place the order?",dir:"outbound",time:"10:35 AM",status:"delivered"}].map((msg,i) => (
                  <div key={i} style={{ display:"flex", justifyContent:msg.dir==="outbound"?"flex-end":"flex-start" }}>
                    <div style={{ maxWidth:"70%", padding:"7px 10px", borderRadius:T.rmd, background:msg.dir==="outbound"?T.brandSoft:th.muted, borderBottomRightRadius:msg.dir==="outbound"?0:T.rmd, borderBottomLeftRadius:msg.dir==="outbound"?T.rmd:0 }}>
                      <div style={{ fontSize:10, color:th.text, lineHeight:1.4, marginBottom:2 }}>{msg.text}</div>
                      <div style={{ display:"flex", alignItems:"center", gap:3, justifyContent:"flex-end" }}>
                        <span style={{ fontSize:8, color:th.textMuted }}>{msg.time}</span>
                        {msg.status==="read" && <span style={{ fontSize:9, color:T.cyan }}>✓✓</span>}
                        {msg.status==="delivered" && <span style={{ fontSize:9, color:th.textSec }}>✓✓</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 12px", borderTop:`1px solid ${th.border}`, background:th.surface }}>
                <span style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>+</span>
                <div style={{ flex:1, height:34, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:18, display:"flex", alignItems:"center", padding:"0 12px", fontSize:10, color:th.textMuted }}>Type a message...</div>
                <span style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>📌</span>
                <span style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>🔗</span>
                <div style={{ width:32,height:32,borderRadius:"50%",background:T.brand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",opacity:0.4,cursor:"default" }}>➤</div>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "chat-send-pending": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ width:32,height:32,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:th.textMuted }}>P</div>
              <span style={{ fontSize:11, fontWeight:600, color:th.text }}>Priya Sharma</span>
            </div>
            <div style={{ flex:1, padding:"10px 14px", overflow:"auto", display:"flex", flexDirection:"column", gap:8, justifyContent:"flex-end" }}>
              <div style={{ display:"flex", justifyContent:"flex-end" }}>
                <div style={{ maxWidth:"70%", padding:"7px 10px", borderRadius:T.rmd, background:T.brandSoft, borderBottomRightRadius:0 }}>
                  <div style={{ fontSize:10, color:th.text, lineHeight:1.4, marginBottom:2 }}>Sure, I'll take it!</div>
                  <div style={{ display:"flex", alignItems:"center", gap:3, justifyContent:"flex-end" }}>
                    <span style={{ fontSize:8, color:th.textMuted }}>10:36 AM</span>
                    <span style={{ fontSize:9, color:th.textMuted }}>⏱</span>
                  </div>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"flex-end" }}>
                <div style={{ maxWidth:"70%", padding:"7px 10px", borderRadius:T.rmd, background:T.brandSoft, borderBottomRightRadius:0 }}>
                  <div style={{ fontSize:10, color:th.text, lineHeight:1.4 }}>Please deliver to my address: 123 Main Street</div>
                  <div style={{ display:"flex", alignItems:"center", gap:3, justifyContent:"flex-end", marginTop:2 }}>
                    <span style={{ fontSize:8, color:th.textMuted }}>10:36 AM</span>
                    <span style={{ fontSize:9, color:th.textMuted }}>⏱</span>
                  </div>
                  <div style={{ padding:"4px 8px", background:T.redSoft, borderRadius:4, marginTop:4, fontSize:8, color:T.red, display:"flex", gap:4, alignItems:"center" }}>
                    Message not delivered
                    <span style={{ fontWeight:600, cursor:"default" }}>Retry</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "chat-send-error": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ width:32,height:32,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:th.textMuted }}>P</div>
              <span style={{ fontSize:11, fontWeight:600, color:th.text }}>Priya Sharma</span>
            </div>
            <div style={{ flex:1, padding:"10px 14px", overflow:"auto", display:"flex", flexDirection:"column", gap:8, justifyContent:"flex-end" }}>
              <div style={{ display:"flex", justifyContent:"flex-start" }}>
                <div style={{ maxWidth:"70%", padding:"7px 10px", borderRadius:T.rmd, background:th.muted }}>
                  <div style={{ fontSize:10, color:th.text }}>Yes please!</div>
                </div>
              </div>
              <div style={{ display:"flex", justifyContent:"flex-end" }}>
                <div style={{ maxWidth:"70%", padding:"7px 10px", borderRadius:T.rmd, background:T.brandSoft, borderBottomRightRadius:0 }}>
                  <div style={{ fontSize:10, color:th.text }}>Great, I'll send you the payment link.</div>
                  <div style={{ display:"flex", alignItems:"center", gap:3, justifyContent:"flex-end", marginTop:2 }}>
                    <span style={{ fontSize:8, color:th.textMuted }}>10:37 AM</span>
                    <span style={{ fontSize:9, color:T.red }}>!️</span>
                  </div>
                  <div style={{ padding:"4px 8px", background:T.redSoft, borderRadius:4, marginTop:4, fontSize:8, color:T.red, display:"flex", gap:6, alignItems:"center", justifyContent:"space-between" }}>
                    <span>Message not delivered</span>
                    <span style={{ fontWeight:600, cursor:"default", color:T.red }}>Retry</span>
                  </div>
                </div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:6, padding:"8px 12px", borderTop:`1px solid ${th.border}`, background:th.surface }}>
              <span style={{ fontSize:14, color:th.textMuted, cursor:"default" }}>+</span>
              <div style={{ flex:1, height:34, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:18, display:"flex", alignItems:"center", padding:"0 12px", fontSize:10, color:th.textMuted }}>Type a message...</div>
              <div style={{ width:32,height:32,borderRadius:"50%",background:T.brand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,color:"#fff",cursor:"default" }}>➤</div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "chat-composer": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ width:32,height:32,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:th.textMuted }}>S</div>
              <span style={{ fontSize:11, fontWeight:600, color:th.text }}>Sneha Patel</span>
            </div>
            <div style={{ flex:1 }} />
            <div style={{ padding:"8px 12px", borderTop:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                <div style={{ fontSize:14, color:th.textMuted, cursor:"default", padding:4 }}>+</div>
                <div style={{ flex:1, minHeight:34, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:18, display:"flex", flexWrap:"wrap", alignItems:"center", padding:"4px 12px", gap:2 }}>
                  {["Hey! Can you deliver by tomorrow?"].map((t,i) => (
                    <span key={i} style={{ fontSize:10, color:th.text, lineHeight:1.4 }}>{t}</span>
                  ))}
                </div>
              </div>
              <div style={{ display:"flex", gap:10, marginTop:6, padding:"0 2px" }}>
                <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>📷</span>
                <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>📄</span>
                <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>📋</span>
                <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>🔗</span>
                <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>📍</span>
                <div style={{ flex:1 }} />
                <div style={{ width:30,height:30,borderRadius:"50%",background:T.brand,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:"#fff",cursor:"default" }}>➤</div>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "chat-reactions": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:420, position:"relative" }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ width:32,height:32,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:10,color:th.textMuted }}>P</div>
              <span style={{ fontSize:11, fontWeight:600, color:th.text }}>Priya Sharma</span>
              <div style={{ flex:1 }} />
              <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>🔼</span>
            </div>
            <div style={{ flex:1, padding:"10px 14px", display:"flex", flexDirection:"column", gap:8 }}>
              <div style={{ display:"flex", justifyContent:"flex-start" }}>
                <div style={{ maxWidth:"70%", padding:"7px 10px", borderRadius:T.rmd, background:th.muted, position:"relative" }}>
                  <div style={{ fontSize:10, color:th.text }}>Thanks for the quick response!</div>
                  <div style={{ fontSize:8, color:th.textMuted, marginTop:2 }}>10:38 AM</div>
                  <div style={{ position:"absolute", bottom:-8, left:4, background:th.surface, border:`1px solid ${th.border}`, borderRadius:8, padding:"1px 4px", fontSize:8, cursor:"default", display:"flex", gap:2 }}>👍</div>
                </div>
              </div>
            </div>
            <div style={{ position:"absolute", bottom:60, right:40, background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, boxShadow:"0 4px 16px rgba(0,0,0,0.15)", padding:"6px 10px" }}>
              <div style={{ display:"flex", gap:6 }}>
                {["👍","❤️","😂","😮","😢"].map((e,i) => (
                  <span key={i} style={{ fontSize:18, cursor:"default", padding:2 }}>{e}</span>
                ))}
              </div>
              <div style={{ borderTop:`1px solid ${th.border}`, marginTop:6, paddingTop:6, display:"flex", gap:8, fontSize:9, color:th.textSec }}>
                <span style={{ cursor:"default" }}>Reply</span>
                <span style={{ cursor:"default" }}>Copy</span>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "chat-socket-banner": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="inbox">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400 }}>
            <div style={{ padding:"6px 12px", background:T.amberSoft, borderBottom:`1px solid ${T.amber}`, fontSize:9, color:T.amber, textAlign:"center", display:"flex", alignItems:"center", justifyContent:"center", gap:4 }}>
              <span style={{ width:8,height:8,borderRadius:"50%",background:T.amber,display:"inline-block" }} />
              Reconnecting…
            </div>
            <div style={{ flex:1, display:"flex", alignItems:"center", justifyContent:"center" }}>
              <div style={{ fontSize:10, color:th.textMuted }}>Waiting for connection…</div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    // ── F4: TEMPLATES ─────────────────────────────────────────────────────────
    "template-gated": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="templates">
          <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:24, minHeight:400 }}>
            <div style={{ width:64,height:64,borderRadius:T.rmd, background:th.muted, marginBottom:12, display:"flex", alignItems:"center", justifyContent:"center", fontSize:24 }}>🔒</div>
            <div style={{ fontSize:13, fontWeight:500, color:th.text, marginBottom:4 }}>Connect WhatsApp First</div>
            <div style={{ fontSize:10, color:th.textSec, textAlign:"center", marginBottom:16, maxWidth:260 }}>You need to connect your WhatsApp Business account before managing templates.</div>
            <MockBtn label="Set up WhatsApp" type="primary" style={{width:"100%",maxWidth:200}} />
            <div style={{ fontSize:9, color:th.textMuted, marginTop:8 }}>Activate subscription to manage templates</div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "template-list": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="templates">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:mob?400:480 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 14px", borderBottom:`1px solid ${th.border}` }}>
              <span style={{ fontSize:13, fontWeight:600, color:th.text }}>Templates</span>
              <MockBtn label="+ Create" type="primary" style={{padding:"6px 12px",fontSize:9}} />
            </div>
            {!mob && (
              <div style={{ display:"flex", gap:6, padding:"6px 14px", borderBottom:`1px solid ${th.border}` }}>
                <div style={{ flex:1, height:28, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:5, display:"flex", alignItems:"center", padding:"0 8px", fontSize:9, color:th.textMuted }}>🔍 Search templates...</div>
                <div style={{ width:90,height:28, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:5, display:"flex", alignItems:"center", padding:"0 8px", fontSize:9, color:th.textMuted, justifyContent:"space-between" }}>Language ▼</div>
                <div style={{ width:90,height:28, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:5, display:"flex", alignItems:"center", padding:"0 8px", fontSize:9, color:th.textMuted, justifyContent:"space-between" }}>Status ▼</div>
              </div>
            )}
            <div style={{ flex:1, overflow:"auto" }}>
              {[
                {name:"Order Confirmation", category:"Transactional", lang:"English", status:"Approved"},
                {name:"Shipping Update", category:"Transactional", lang:"Hindi", status:"Pending"},
                {name:"Welcome Message", category:"Marketing", lang:"English", status:"Approved"},
                {name:"Festival Offer", category:"Marketing", lang:"English", status:"Rejected"},
              ].map((t,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"8px 14px", borderBottom:`1px solid ${th.border}`, cursor:"default" }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:500, color:th.text }}>{t.name}</div>
                    <div style={{ fontSize:8, color:th.textMuted }}>{t.category} · {t.lang}</div>
                  </div>
                  <MockBadge label={t.status} type={t.status==="Approved"?"active":t.status==="Pending"?"pending":"error"} />
                  <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>🖉</span>
                </div>
              ))}
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "template-editor": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="templates">
          <div style={{ flex:1, display:"flex", flexDirection:mob?"column":"row", minHeight:mob?500:480 }}>
            <div style={{ flex:1, padding:"14px 16px", overflow:"auto" }}>
              <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Create Template</div>
              <div style={{ marginBottom:8 }}>
                <MockField label="Template Name" val="" req />
              </div>
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Category</div>
                <div style={{ height:32, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:6, display:"flex", alignItems:"center", padding:"0 10px", justifyContent:"space-between", fontSize:10, color:th.text }}>Marketing <span style={{ color:th.textMuted, fontSize:8 }}>▼</span></div>
              </div>
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Language</div>
                <div style={{ height:32, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:6, display:"flex", alignItems:"center", padding:"0 10px", justifyContent:"space-between", fontSize:10, color:th.text }}>English <span style={{ color:th.textMuted, fontSize:8 }}>▼</span></div>
              </div>
              <div style={{ marginBottom:6 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:10, color:th.textSec, marginBottom:4 }}>
                  <span>Body Message</span>
                  <span style={{ color:th.textMuted, fontSize:8 }}>128/1024</span>
                </div>
                <div style={{ minHeight:80, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:6, padding:"8px 10px", fontSize:10, color:th.text, lineHeight:1.5 }}>
                  Hi {'{{1}}'}! Check out our latest offers. Use code {'{{2}}'} for a special discount!
                </div>
              </div>
              <div style={{ display:"flex", gap:4, marginBottom:10 }}>
                {['{{1}}','{{2}}'].map((p,i) => (
                  <div key={i} style={{ padding:"2px 6px", background:T.violetSoft, color:T.violet, fontSize:8, borderRadius:4, border:`1px solid ${T.violet}33`, cursor:"default" }}>{p}</div>
                ))}
                <span style={{ padding:"2px 6px", fontSize:8, color:T.violet, cursor:"default" }}>+ Insert</span>
              </div>
              <div style={{ display:"flex", gap:8, marginTop:4 }}>
                <MockBtn label="Save Draft" type="secondary" style={{flex:1}} />
                <MockBtn label="Submit to Meta" type="primary" style={{flex:1}} />
              </div>
            </div>
            {!mob && (
              <div style={{ width:280, borderLeft:`1px solid ${th.border}`, padding:"14px 16px", background:th.surface }}>
                <div style={{ fontSize:11, fontWeight:600, color:th.text, marginBottom:10 }}>Preview</div>
                <div style={{ background:th.muted, borderRadius:T.rmd, padding:12, maxWidth:220 }}>
                  <div style={{ fontWeight:500, fontSize:9, color:th.textSec, marginBottom:6 }}>ZiBuzz</div>
                  <div style={{ fontSize:10, color:th.text, lineHeight:1.5, marginBottom:4 }}>Hi <strong>Ravi</strong>! Check out our latest offers. Use code <strong>ZIBUZZ20</strong> for a special discount!</div>
                  <div style={{ fontSize:8, color:th.textMuted }}>Tap to reply</div>
                </div>
              </div>
            )}
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "template-library": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="templates">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:mob?400:460 }}>
            <div style={{ padding:"8px 14px", borderBottom:`1px solid ${th.border}`, fontSize:13, fontWeight:600, color:th.text }}>Template Library</div>
            <div style={{ display:"flex", gap:4, padding:"6px 14px", borderBottom:`1px solid ${th.border}`, fontSize:9 }}>
              {["All","Marketing","Transactional","Utility"].map((c,i) => (
                <div key={c} style={{ padding:"3px 10px", borderRadius:12, cursor:"default", background:i===0?T.violetSoft:"transparent", color:i===0?T.violet:th.textSec, border:`1px solid ${i===0?T.violet:th.border}` }}>{c}</div>
              ))}
            </div>
            <div style={{ flex:1, overflow:"auto", display:"flex", flexDirection:"column", gap:6, padding:10 }}>
              {[
                {name:"Flash Sale Alert", cat:"Marketing", desc:"Notify customers about limited-time offers"},
                {name:"Order Confirmation", cat:"Transactional", desc:"Confirm order details and ETA"},
                {name:"Feedback Request", cat:"Marketing", desc:"Ask customers to rate their experience"},
              ].map((t,i) => (
                <div key={i} style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rsm, padding:12, cursor:"default" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                    <span style={{ fontSize:11, fontWeight:500, color:th.text }}>{t.name}</span>
                    <span style={{ fontSize:8, padding:"1px 6px", borderRadius:4, background:T.violetSoft, color:T.violet }}>{t.cat}</span>
                  </div>
                  <div style={{ fontSize:9, color:th.textSec, marginBottom:8 }}>{t.desc}</div>
                  <MockBtn label="Use this template" type="secondary" style={{width:"100%",fontSize:9}} />
                </div>
              ))}
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "template-send-modal": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="templates">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, position:"relative" }}>
            <div style={{ flex:1, filter:"blur(1px)", opacity:0.3 }}>
              <div style={{ padding:"8px 14px", borderBottom:`1px solid ${th.border}`, fontSize:13, fontWeight:600, color:th.text }}>Templates</div>
            </div>
            <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:mob?"90%":380, background:th.surface, border:`1px solid ${th.borderMd}`, borderRadius:T.rmd, boxShadow:"0 8px 32px rgba(0,0,0,0.2)", padding:16, zIndex:10 }}>
              <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Send Template</div>
              <div style={{ marginBottom:8 }}>
                <MockField label="Recipient" val="Priya Sharma" req />
              </div>
              <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Template: <strong style={{ color:th.text }}>Order Confirmation</strong></div>
              <div style={{ marginBottom:6 }}>
                <MockField label="{{1}} — Customer Name" val="Priya" req />
              </div>
              <div style={{ marginBottom:10 }}>
                <MockField label="{{2}} — Order ID" val="ORD-1042" req />
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <div style={{ flex:1 }}><MockBtn label="Cancel" type="secondary" style={{width:"100%"}} /></div>
                <div style={{ flex:1 }}><MockBtn label="Send" type="primary" style={{width:"100%"}} /></div>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "template-send-error": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="templates">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, position:"relative" }}>
            <div style={{ flex:1, filter:"blur(1px)", opacity:0.3 }}>
              <div style={{ padding:"8px 14px", borderBottom:`1px solid ${th.border}`, fontSize:13, fontWeight:600, color:th.text }}>Templates</div>
            </div>
            <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:mob?"90%":380, background:th.surface, border:`1px solid ${th.borderMd}`, borderRadius:T.rmd, boxShadow:"0 8px 32px rgba(0,0,0,0.2)", padding:16, zIndex:10 }}>
              <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Send Template</div>
              <div style={{ marginBottom:8 }}>
                <MockField label="Recipient" val="Priya Sharma" req />
              </div>
              <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Template: <strong style={{ color:th.text }}>Order Confirmation</strong></div>
              <div style={{ marginBottom:6 }}>
                <div style={{ fontSize:10, color:th.textSec, marginBottom:2 }}>{'{{1}}'} — Customer Name</div>
                <div style={{ height:32, background:th.muted, border:`1px solid ${T.red}`, borderRadius:6, display:"flex", alignItems:"center", padding:"0 10px", fontSize:10, color:th.text }}> </div>
                <div style={{ fontSize:8, color:T.red, marginTop:2 }}>This field is required</div>
              </div>
              <div style={{ marginBottom:10 }}>
                <MockField label="{{2}} — Order ID" val="" req />
                <div style={{ fontSize:8, color:T.red, marginTop:2 }}>This field is required</div>
              </div>
              <div style={{ display:"flex", gap:8 }}>
                <div style={{ flex:1 }}><MockBtn label="Cancel" type="secondary" style={{width:"100%"}} /></div>
                <div style={{ flex:1 }}><MockBtn label="Send" type="primary" style={{width:"100%",opacity:0.4}} disabled /></div>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    // ── F5: SUBSCRIPTION ──────────────────────────────────────────────────────
    "subs-overview": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="subscription">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:mob?500:480, maxWidth:mob?"100%":720, margin:mob?0:"0 auto", width:"100%" }}>
            <div style={{ padding:"14px 16px 8px" }}>
              <div style={{ fontSize:15, fontWeight:600, color:th.text }}>Choose Your Plan</div>
              <div style={{ fontSize:10, color:th.textMuted, marginTop:2 }}>Pick the plan that works for you</div>
            </div>
            <div style={{ padding:"0 16px", marginBottom:12 }}>
              <div style={{ display:"flex", background:th.muted, borderRadius:8, padding:3 }}>
                {["Monthly","Quarterly","Annual"].map((c,i) => (
                  <div key={c} style={{ flex:1, textAlign:"center", padding:"5px 0", fontSize:10, borderRadius:6, cursor:"default", background:i===0?th.surface:"transparent", color:i===0?th.text:th.textSec, fontWeight:i===0?500:400, boxShadow:i===0?`0 1px 3px rgba(0,0,0,0.1)`:"none" }}>{c}</div>
                ))}
              </div>
            </div>
            <div style={{ flex:1, display:"flex", flexDirection:mob?"column":"row", gap:12, padding:"0 16px 16px" }}>
              {[
                {name:"Solo", price:"₹499", desc:"For individual merchants", features:["500 customers","200 msgs/day","1 team member"]},
                {name:"Galaxy", price:"₹1,999", desc:"For growing businesses", features:["Unlimited customers","Unlimited msgs","Up to 10 team members","Analytics dashboard"], popular:true},
              ].map((p,i) => (
                <div key={i} style={{ flex:1, background:th.surface, border:`2px solid ${p.popular?T.brand:th.border}`, borderRadius:T.rmd, padding:16, position:"relative" }}>
                  {p.popular && <div style={{ position:"absolute", top:-8, right:12, background:T.brand, color:"#fff", fontSize:8, padding:"1px 8px", borderRadius:8, fontWeight:600 }}>Most Popular</div>}
                  <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:2 }}>{p.name}</div>
                  <div style={{ fontSize:10, color:th.textSec, marginBottom:8 }}>{p.desc}</div>
                  <div style={{ fontSize:22, fontWeight:700, color:th.text, marginBottom:2 }}>{p.price}<span style={{ fontSize:10, fontWeight:400, color:th.textMuted }}>/mo</span></div>
                  <div style={{ fontSize:8, color:T.green, marginBottom:10 }}>Save 20% with annual</div>
                  <div style={{ marginBottom:10 }}>
                    {p.features.map((f,j) => (
                      <div key={j} style={{ fontSize:9, color:th.textSec, padding:"3px 0", display:"flex", gap:4 }}>
                        <span style={{ color:T.green }}>✓</span> {f}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize:9, color:T.brand, cursor:"default", marginBottom:8 }}>Show more</div>
                  <MockBtn label="Get Started" type={p.popular?"primary":"secondary"} style={{width:"100%"}} />
                </div>
              ))}
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "subs-plan-cards": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="subscription">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, maxWidth:mob?"100%":720, margin:mob?0:"0 auto", width:"100%" }}>
            <div style={{ padding:"14px 16px 8px" }}>
              <div style={{ fontSize:15, fontWeight:600, color:th.text }}>Subscription Plans</div>
            </div>
            <div style={{ flex:1, display:"flex", flexDirection:mob?"column":"row", gap:12, padding:"0 16px 16px" }}>
              {[
                {name:"Solo", price:"₹499", savings:false, features:["500 customers","200 msgs/day","1 team member","Basic analytics"]},
                {name:"Galaxy", price:"₹1,999", savings:true, features:["Unlimited customers","Unlimited msgs/day","Up to 10 team members","Advanced analytics","API access","Priority support"], popular:true},
              ].map((p,i) => (
                <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", background:th.surface, border:`2px solid ${p.popular?T.brand:th.border}`, borderRadius:T.rmd, padding:16 }}>
                  <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:4 }}>{p.name}</div>
                  <div style={{ fontSize:20, fontWeight:700, color:th.text, marginBottom:2 }}>{p.price}<span style={{ fontSize:10, fontWeight:400, color:th.textMuted }}>/mo</span></div>
                  {p.savings && <div style={{ fontSize:8, color:T.green, background:T.greenSoft, padding:"1px 6px", borderRadius:4, display:"inline-block", marginBottom:8, width:"fit-content" }}>Save 20% with annual</div>}
                  {!p.savings && <div style={{ height:16, marginBottom:8 }} />}
                  <div style={{ marginBottom:10 }}>
                    {p.features.map((f,j) => (
                      <div key={j} style={{ fontSize:9, color:th.textSec, padding:"3px 0", display:"flex", gap:4 }}>
                        <span style={{ color:T.green }}>✓</span> {f}
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize:9, color:T.brand, cursor:"default", marginBottom:"auto" }}>Show less</div>
                  <div style={{ marginTop:8 }}>
                    <MockBtn label={p.popular?"Renew Plan":"Get Started"} type={p.popular?"primary":"secondary"} style={{width:"100%"}} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "subs-trial": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="subscription">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, maxWidth:mob?"100%":480, margin:mob?0:"0 auto", width:"100%" }}>
            <div style={{ padding:"14px 16px 8px" }}>
              <div style={{ fontSize:15, fontWeight:600, color:th.text }}>Solo Plan</div>
            </div>
            <div style={{ padding:"0 16px 16px" }}>
              <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16 }}>
                <div style={{ fontSize:20, fontWeight:700, color:th.text, marginBottom:8 }}>₹499<span style={{ fontSize:10, fontWeight:400, color:th.textMuted }}>/mo</span></div>
                <div style={{ marginBottom:10 }}>
                  {["500 customers","200 msgs/day","1 team member","Basic analytics"].map((f,j) => (
                    <div key={j} style={{ fontSize:9, color:th.textSec, padding:"3px 0", display:"flex", gap:4 }}>
                      <span style={{ color:T.green }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <div style={{ padding:"10px 14px", background:T.tealSoft, border:`1px solid ${T.teal}33`, borderRadius:T.rsm, marginBottom:10 }}>
                  <div style={{ fontSize:11, fontWeight:500, color:T.teal, marginBottom:2 }}>Activate 7-Day Trial — Free</div>
                  <div style={{ fontSize:8, color:T.teal, opacity:0.8 }}>No payment required. Cancel anytime.</div>
                </div>
                <MockBtn label="Get Started" type="primary" style={{width:"100%"}} />
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "subs-cta-states": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="subscription">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, maxWidth:mob?"100%":480, margin:mob?0:"0 auto", width:"100%", padding:"20px 16px" }}>
            <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16, marginBottom:12 }}>
              <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:6 }}>CTA State Matrix</div>
              <div style={{ fontSize:9, color:th.textSec, marginBottom:10 }}>Each plan CTA resolves based on auth + subscription state:</div>
              {[
                {condition:"Not authenticated", label:"Login to Subscribe", desc:"Redirects to login with returnUrl"},
                {condition:"Same plan + cycle", label:"Renew Plan", desc:"Opens checkout for renewal"},
                {condition:"Different plan/cycle", label:"Get Started", desc:"Opens checkout for new plan"},
                {condition:"No active plan", label:"Get Started", desc:"Opens checkout"},
              ].map((row,i) => (
                <div key={i} style={{ padding:"6px 0", borderBottom:i<3?`1px solid ${th.border}`:"none" }}>
                  <div style={{ fontSize:9, color:th.textMuted, marginBottom:2 }}>{row.condition}</div>
                  <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                    <MockBtn label={row.label} type={i===0?"secondary":"primary"} style={{fontSize:9,padding:"4px 10px"}} />
                    <span style={{ fontSize:8, color:th.textSec }}>{row.desc}</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16 }}>
              <div style={{ fontSize:11, fontWeight:500, color:th.text, marginBottom:4 }}>Current Evaluation</div>
              <div style={{ fontSize:9, color:th.textSec, marginBottom:6 }}>New user, no active plan</div>
              <MockBtn label="Get Started" type="primary" style={{width:"100%"}} />
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "subs-checkout": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="subscription">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, maxWidth:mob?"100%":480, margin:mob?0:"0 auto", width:"100%" }}>
            <div style={{ padding:"14px 16px 8px" }}>
              <div style={{ fontSize:15, fontWeight:600, color:th.text }}>Checkout</div>
              <div style={{ fontSize:10, color:th.textMuted, marginTop:2 }}>Galaxy Plan — Monthly</div>
            </div>
            <div style={{ padding:"0 16px 16px" }}>
              <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16, marginBottom:12 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <span style={{ fontSize:10, color:th.textSec }}>Galaxy Monthly</span>
                  <span style={{ fontSize:12, fontWeight:600, color:th.text }}>₹1,999</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
                  <span style={{ fontSize:10, color:th.textSec }}>GST (18%)</span>
                  <span style={{ fontSize:10, color:th.text }}>₹360</span>
                </div>
                <div style={{ borderTop:`1px solid ${th.border}`, paddingTop:8, display:"flex", justifyContent:"space-between" }}>
                  <span style={{ fontSize:11, fontWeight:600, color:th.text }}>Total</span>
                  <span style={{ fontSize:13, fontWeight:700, color:th.text }}>₹2,359</span>
                </div>
              </div>
              <div style={{ background:th.muted, borderRadius:T.rmd, padding:20, marginBottom:12, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:120 }}>
                <div style={{ fontSize:10, color:th.textMuted, marginBottom:6 }}>Cashfree Payment</div>
                <div style={{ width:40,height:40,border:`2px solid ${th.border}`,borderTop:`2px solid ${T.brand}`,borderRadius:"50%",marginBottom:6 }} />
                <div style={{ fontSize:9, color:th.textSec }}>Redirecting to payment…</div>
              </div>
              <div style={{ background:T.amberSoft, border:`1px solid ${T.amber}33`, borderRadius:T.rsm, padding:10, marginBottom:12 }}>
                <div style={{ fontSize:9, fontWeight:500, color:T.amber, marginBottom:4 }}>Payment Pending</div>
                <div style={{ fontSize:8, color:th.textSec, marginBottom:6 }}>Order: ORD-ZB-20260529-001</div>
                <MockBtn label="Verify Payment" type="primary" style={{width:"100%",fontSize:9}} />
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "subs-verify": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="subscription">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, maxWidth:mob?"100%":480, margin:mob?0:"0 auto", width:"100%" }}>
            <div style={{ padding:"14px 16px 8px" }}>
              <div style={{ fontSize:15, fontWeight:600, color:th.text }}>Verify Payment</div>
            </div>
            <div style={{ padding:"0 16px 16px" }}>
              <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16 }}>
                <div style={{ fontSize:10, color:th.textSec, marginBottom:6 }}>Order Reference</div>
                <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:10 }}>ORD-ZB-20260529-001</div>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                  <span style={{ fontSize:10, color:th.textSec }}>Plan</span>
                  <span style={{ fontSize:10, fontWeight:500, color:th.text }}>Galaxy — Monthly</span>
                </div>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:12 }}>
                  <span style={{ fontSize:10, color:th.textSec }}>Amount Paid</span>
                  <span style={{ fontSize:11, fontWeight:600, color:th.text }}>₹2,359</span>
                </div>
                <div style={{ padding:"8px 12px", background:T.greenSoft, borderRadius:T.rsm, marginBottom:10 }}>
                  <div style={{ fontSize:9, color:T.green, fontWeight:500 }}>Payment confirmed — your plan is active!</div>
                </div>
                <MockBtn label="Done" type="primary" style={{width:"100%"}} />
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "subs-payment-error": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="subscription">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, maxWidth:mob?"100%":480, margin:mob?0:"0 auto", width:"100%" }}>
            <div style={{ padding:"14px 16px 8px" }}>
              <div style={{ fontSize:15, fontWeight:600, color:th.text }}>Subscription</div>
            </div>
            <div style={{ padding:"0 16px 16px" }}>
              <div style={{ background:T.redSoft, border:`1px solid ${T.red}33`, borderRadius:T.rmd, padding:16, marginBottom:12 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                  <span style={{ fontSize:14 }}>!</span>
                  <span style={{ fontSize:11, fontWeight:500, color:T.red }}>Payment Declined</span>
                </div>
                <div style={{ fontSize:9, color:th.textSec, marginBottom:10, lineHeight:1.5 }}>
                  Your bank declined the transaction. Please try a different payment method or contact your bank.
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <div style={{ flex:1 }}><MockBtn label="Try Again" type="primary" style={{width:"100%",fontSize:9}} /></div>
                  <div style={{ flex:1 }}><MockBtn label="Contact Support" type="secondary" style={{width:"100%",fontSize:9}} /></div>
                </div>
              </div>
              <div style={{ background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16, marginBottom:8 }}>
                <div style={{ fontSize:11, fontWeight:500, color:th.text, marginBottom:6 }}>Try a different plan</div>
                <div style={{ fontSize:9, color:th.textSec }}>Plan cards remain visible below so you can attempt a different plan.</div>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    // ── F6: ADMINISTRATION ────────────────────────────────────────────────────
    "team-admin": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="team">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:mob?400:460 }}>
            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"8px 14px", borderBottom:`1px solid ${th.border}` }}>
              <span style={{ fontSize:13, fontWeight:600, color:th.text }}>Team</span>
              <MockBtn label="+ Add Member" type="primary" style={{fontSize:9,padding:"5px 10px"}} />
            </div>
            <div style={{ flex:1, overflow:"auto" }}>
              {[
                {name:"You", role:"Admin", mobile:"+91 98765 43210", status:"Active", resigned:null},
                {name:"Rahul S.", role:"Agent", mobile:"+91 87654 32109", status:"Active", resigned:null},
                {name:"Priya M.", role:"Agent", mobile:"+91 76543 21098", status:"Resigned", resigned:"2026-05-15"},
              ].map((m,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 14px", borderBottom:`1px solid ${th.border}`, cursor:"default" }}>
                  <div style={{ width:32,height:32,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:th.textMuted }}>{m.name[0]}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:500, color:th.text }}>{m.name}</div>
                    <div style={{ fontSize:9, color:th.textSec }}>{m.mobile}</div>
                    {m.resigned && <div style={{ fontSize:8, color:th.textMuted }}>Resigned: {m.resigned}</div>}
                  </div>
                  <MockBadge label={m.role} type={m.role==="Admin"?"active":"pending"} />
                  {m.status==="Active" && <span style={{ fontSize:12, color:th.textMuted, cursor:"default" }}>🗑</span>}
                </div>
              ))}
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "team-member": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="team">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:mob?350:400 }}>
            <div style={{ padding:"8px 14px", borderBottom:`1px solid ${th.border}`, fontSize:13, fontWeight:600, color:th.text }}>Team</div>
            <div style={{ flex:1, overflow:"auto" }}>
              {[
                {name:"You", role:"Team Member", mobile:"+91 98765 43210", status:"Active"},
                {name:"Ravi S.", role:"Admin", mobile:"+91 87654 32109", status:"Active"},
                {name:"Anita K.", role:"Agent", mobile:"+91 76543 21098", status:"Active"},
              ].map((m,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:8, padding:"9px 14px", borderBottom:`1px solid ${th.border}`, cursor:"default" }}>
                  <div style={{ width:32,height:32,borderRadius:"50%",background:th.muted,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:th.textMuted }}>{m.name[0]}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:500, color:th.text }}>{m.name}</div>
                    <div style={{ fontSize:9, color:th.textSec }}>{m.mobile}</div>
                  </div>
                  <MockBadge label={m.role} type={m.role==="Admin"?"active":"pending"} />
                  {m.role==="Team Member" && m.name==="You" && <span style={{ fontSize:10, color:T.coral, cursor:"default", textDecoration:"underline" }}>Resign</span>}
                </div>
              ))}
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "team-add-form": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="team">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, position:"relative" }}>
            <div style={{ flex:1, filter:"blur(1px)", opacity:0.3 }}>
              <div style={{ padding:"8px 14px", borderBottom:`1px solid ${th.border}`, fontSize:13, fontWeight:600, color:th.text }}>Team</div>
            </div>
            <div style={{ position:"absolute", bottom:0, left:0, right:0, background:th.surface, borderTop:`1px solid ${th.borderMd}`, borderRadius:`${T.rmd} ${T.rmd} 0 0`, padding:16, boxShadow:"0 -4px 16px rgba(0,0,0,0.1)" }}>
              <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Add Team Member</div>
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Country Code</div>
                <div style={{ height:34, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:6, display:"flex", alignItems:"center", padding:"0 10px", justifyContent:"space-between", fontSize:10, color:th.text }}>
                  🇮🇳 +91 <span style={{ fontSize:8, color:th.textMuted }}>▼</span>
                </div>
              </div>
              <div style={{ marginBottom:12 }}>
                <MockField label="Mobile Number" val="" req />
              </div>
              <MockBtn label="Add" type="primary" style={{width:"100%"}} />
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "team-add-error": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="team">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, position:"relative" }}>
            <div style={{ flex:1, filter:"blur(1px)", opacity:0.3 }}>
              <div style={{ padding:"8px 14px", borderBottom:`1px solid ${th.border}`, fontSize:13, fontWeight:600, color:th.text }}>Team</div>
            </div>
            <div style={{ position:"absolute", bottom:0, left:0, right:0, background:th.surface, borderTop:`1px solid ${th.borderMd}`, borderRadius:`${T.rmd} ${T.rmd} 0 0`, padding:16, boxShadow:"0 -4px 16px rgba(0,0,0,0.1)" }}>
              <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Add Team Member</div>
              <div style={{ marginBottom:8 }}>
                <div style={{ fontSize:10, color:th.textSec, marginBottom:4 }}>Country Code</div>
                <div style={{ height:34, background:th.muted, border:`1px solid ${th.borderMd}`, borderRadius:6, display:"flex", alignItems:"center", padding:"0 10px", justifyContent:"space-between", fontSize:10, color:th.text }}>
                  🇮🇳 +91 <span style={{ fontSize:8, color:th.textMuted }}>▼</span>
                </div>
              </div>
              <div style={{ marginBottom:4 }}>
                <MockField label="Mobile Number" val="123" req />
                <div style={{ fontSize:8, color:T.red, marginTop:2 }}>Enter a valid mobile number (6\u201314 digits)</div>
              </div>
              <MockBtn label="Add" type="primary" style={{width:"100%",opacity:0.4}} disabled />
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "profile-overview": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="profile">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:mob?480:500 }}>
            <div style={{ padding:"16px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", gap:12 }}>
              <div style={{ width:44,height:44,borderRadius:"50%",background:T.brandSoft,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:T.brand,fontWeight:600 }}>U</div>
              <div>
                <div style={{ fontSize:14, fontWeight:600, color:th.text }}>Ravi Sharma</div>
                <div style={{ fontSize:10, color:th.textSec }}>+91 98765 43210</div>
                <div style={{ display:"flex", alignItems:"center", gap:4, marginTop:2 }}>
                  <MockBadge label="Admin" type="active" />
                  <span style={{ fontSize:8, color:T.teal }}>\u25CF</span>
                  <span style={{ fontSize:8, color:th.textMuted }}>WhatsApp Connected</span>
                </div>
              </div>
            </div>
            <div style={{ padding:"8px 0" }}>
              {[
                {icon:"💳", label:"Manage Subscription", gate:"Requires connected WhatsApp"},
                {icon:"📋", label:"Manage Templates", gate:"Requires active subscription + WhatsApp"},
                {icon:"📲", label:"WhatsApp Setup", gate:null},
              ].map((a,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderBottom:`1px solid ${th.border}`, cursor:"default" }}>
                  <span style={{ fontSize:14 }}>{a.icon}</span>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:500, color:th.text }}>{a.label}</div>
                    {a.gate && <div style={{ fontSize:8, color:th.textMuted }}>🔒 {a.gate}</div>}
                  </div>
                  <span style={{ fontSize:12, color:th.textMuted }}>›</span>
                </div>
              ))}
            </div>
            <div style={{ borderTop:`1px solid ${th.border}`, padding:"8px 0" }}>
              {[
                {icon:"🔒", label:"Change Password"},
                {icon:"🚪", label:"Logout", danger:true},
              ].map((a,i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", cursor:"default" }}>
                  <span style={{ fontSize:14 }}>{a.icon}</span>
                  <span style={{ fontSize:11, fontWeight:500, color:a.danger?T.red:th.text }}>{a.label}</span>
                  <div style={{ flex:1 }} />
                  <span style={{ fontSize:12, color:th.textMuted }}>›</span>
                </div>
              ))}
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "profile-gating": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="profile">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, position:"relative" }}>
            <div style={{ flex:1, filter:"blur(1px)", opacity:0.3 }}>
              <div style={{ padding:"16px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:44,height:44,borderRadius:"50%",background:T.brandSoft,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:T.brand }}>U</div>
                <div>
                  <div style={{ fontSize:14, fontWeight:600, color:th.text }}>Ravi Sharma</div>
                  <div style={{ fontSize:10, color:th.textSec }}>+91 98765 43210</div>
                </div>
              </div>
            </div>
            <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:mob?"90%":360, background:th.surface, border:`1px solid ${th.borderMd}`, borderRadius:T.rmd, padding:16, boxShadow:"0 8px 32px rgba(0,0,0,0.15)", zIndex:10 }}>
              <div style={{ textAlign:"center", marginBottom:10 }}>
                <div style={{ fontSize:24, marginBottom:6 }}>🔒</div>
                <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:4 }}>Connect WhatsApp First</div>
                <div style={{ fontSize:9, color:th.textSec, lineHeight:1.5 }}>
                  You need to connect your WhatsApp Business account before managing subscriptions.
                </div>
              </div>
              <MockBtn label="Go to WhatsApp Setup" type="primary" style={{width:"100%"}} />
              <div style={{ fontSize:9, color:th.textMuted, textAlign:"center", marginTop:6 }}>Or activate a subscription to manage templates</div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "profile-password": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="profile">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:420, padding:"16px 14px" }}>
            <div style={{ fontSize:14, fontWeight:600, color:th.text, marginBottom:14 }}>Change Password</div>
            <div style={{ marginBottom:8 }}>
              <MockField label="Current Password" val="" req />
            </div>
            <div style={{ marginBottom:6 }}>
              <MockField label="New Password" val="" req />
            </div>
            <div style={{ height:3, width:"40%", background:th.borderMd, borderRadius:2, marginBottom:6 }} />
            <div style={{ marginBottom:12 }}>
              <MockField label="Confirm New Password" val="" req />
            </div>
            <div style={{ marginBottom:8, padding:"8px 12px", background:T.greenSoft, borderRadius:T.rsm, fontSize:9, color:T.green, fontWeight:500 }}>
              Password updated successfully!
            </div>
            <MockBtn label="Save" type="primary" style={{width:"100%"}} />
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
    "profile-logout": () => (
      <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
        <ZibuzzAppShell dark={dark} viewport={viewport} activeTab="profile">
          <div style={{ flex:1, display:"flex", flexDirection:"column", minHeight:400, position:"relative" }}>
            <div style={{ flex:1, filter:"blur(1px)", opacity:0.3 }}>
              <div style={{ padding:"16px 14px", borderBottom:`1px solid ${th.border}`, display:"flex", alignItems:"center", gap:12 }}>
                <div style={{ width:44,height:44,borderRadius:"50%",background:T.brandSoft,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:T.brand }}>U</div>
                <div>
                  <div style={{ fontSize:14, fontWeight:600, color:th.text }}>Ravi Sharma</div>
                  <div style={{ fontSize:10, color:th.textSec }}>+91 98765 43210</div>
                </div>
              </div>
            </div>
            <div style={{ position:"absolute", bottom:"40%", left:"50%", transform:"translate(-50%,0)", width:mob?"90%":340, background:th.surface, border:`1px solid ${th.borderMd}`, borderRadius:T.rmd, padding:16, boxShadow:"0 8px 32px rgba(0,0,0,0.15)", zIndex:10 }}>
              <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:6 }}>Log Out</div>
              <div style={{ fontSize:9, color:th.textSec, marginBottom:14 }}>Are you sure you want to log out? Your session will be cleared.</div>
              <div style={{ display:"flex", gap:8 }}>
                <div style={{ flex:1 }}><MockBtn label="Cancel" type="secondary" style={{width:"100%"}} /></div>
                <div style={{ flex:1 }}>      <MockBtn label="Log Out" type="danger" style={{width:"100%"}} /></div>
              </div>
            </div>
          </div>
        </ZibuzzAppShell>
      </WF>
    ),
  };

  const renderer = screens[screen];
  if (renderer) return renderer();
  return (
    <WF dark={dark} viewport={viewport} slim={slim} domain="zibuzz.ziniz.online">
      <PublicShell dark={dark}>
        <div style={{ padding:20, textAlign:"center" }}>
          <div style={{ fontSize:13, fontWeight:600, color:th.text }}>{screen}</div>
          <div style={{ fontSize:9, color:th.textMuted, marginTop:4 }}>Wireframe pending</div>
        </div>
      </PublicShell>
    </WF>
  );
}

// ─── MAIN WIZARD ───────────────────────────────────────────────────────────────
const SECTIONS = [
  { id:"flows", label:"Flows" },
  { id:"ia", label:"IA" },
  { id:"routes", label:"Routes" },
  { id:"folders", label:"Folders" },
  { id:"shells", label:"Shells" },
  { id:"tech-stack", label:"Tech Stack" },
  { id:"configs", label:"Configs" },
];

export default function ZiBuzzUXWizard() {
  const [dark, setDark] = useState(false);
  const [viewport, setViewport] = useState("desktop");
  const [section, setSection] = useState("flows");
  const [activeFlow, setActiveFlow] = useState("zibuzz-access");
  const [activeStep, setActiveStep] = useState(null);
  const [selectedScreen, setSelectedScreen] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [inspectorMode, setInspectorMode] = useState("off");
  const th = useTheme(dark);

  const currentFlow = FLOWS.find(f => f.id === activeFlow);
  const currentScreen = selectedScreen || (activeStep ? (STEP_TO_SCREEN[activeStep] || activeStep) : null);
  const currentStep = activeStep ? currentFlow?.steps.find(s => s.id === activeStep) : null;

  const flowSidebar = (
    <div>
      {FLOWS.map(flow => (
        <div key={flow.id}>
          <div style={{ padding:"6px 10px", fontSize:10, fontWeight:600, color:flow.id===activeFlow?T.brand:th.textSec, cursor:"default", background:flow.id===activeFlow?T.brandSoft:"transparent", borderRight:flow.id===activeFlow?`2px solid ${T.brand}`:"2px solid transparent" }} onClick={() => { setActiveFlow(flow.id); setActiveStep(null); setSelectedScreen(null); }}>
            {flow.label}
          </div>
          {flow.id===activeFlow && (
            <div style={{ paddingLeft:6 }}>
              {flow.steps.map(step => (
                <div key={step.id} style={{ padding:"4px 10px", fontSize:9, color:activeStep===step.id?th.text:th.textMuted, cursor:"default", background:activeStep===step.id?th.muted:"transparent", borderRadius:4, margin:"1px 4px", display:"flex", alignItems:"center", gap:4 }} onClick={() => { setActiveStep(step.id); setSelectedScreen(STEP_TO_SCREEN[step.id] || step.id); }}>
                  <span style={{ width:6,height:6,borderRadius:"50%",background:activeStep===step.id?T.brand:th.borderMd }} />
                  {step.label}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );

  const inspectorPanel = inspectorMode !== "off" && currentStep ? (
    <div style={{ width:280, borderLeft:`1px solid ${th.border}`, background:th.surface, padding:12, overflow:"auto", flexShrink:0 }}>
      <div style={{ fontSize:10, fontWeight:600, color:th.text, marginBottom:6 }}>Step Brief</div>
      <div style={{ fontSize:9, color:th.textSec, lineHeight:1.5, marginBottom:8 }}>{currentStep.note}</div>
      <div style={{ fontSize:9, color:th.textMuted }}>
        Shell: <strong style={{ color:th.text }}>{currentStep.shell}</strong><br/>
        Screen: <strong style={{ color:th.text }}>{currentScreen}</strong>
      </div>
    </div>
  ) : null;

  return (
    <InspectProvider>
      <WizardChrome
        dark={dark}
        setDark={setDark}
        title="ZiBuzz UX"
        subtitle="Unified Mobile-Web Revamp"
        domain="zibuzz.ziniz.online"
        sections={SECTIONS}
        section={section}
        onSectionChange={setSection}
        viewport={viewport}
        onViewportChange={setViewport}
        sidebarOpen={sidebarOpen}
        onSidebarToggle={() => setSidebarOpen(!sidebarOpen)}
        flowSidebar={flowSidebar}
        inspectorPanel={inspectorPanel}
        inspectorOpen={inspectorMode !== "off"}
      >
        {/* Flows view */}
        {section === "flows" && (
          <div style={{ flex:1, display:"flex", flexDirection:"column" }}>
            <div style={{ padding:"8px 14px", borderBottom:`1px solid ${th.border}`, background:th.surface }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <div style={{ fontSize:12, fontWeight:600, color:th.text }}>{currentFlow?.label}</div>
                <span style={{ fontSize:9, color:th.textMuted }}>|</span>
                <span style={{ fontSize:9, color:th.textSec }}>{currentFlow?.who}</span>
                <div style={{ flex:1 }} />
                <span style={{ fontSize:9, color:th.textMuted }}>{currentFlow?.steps.length} steps</span>
              </div>
            </div>
            <div style={{ flex:1, display:"flex", alignItems:"flex-start", justifyContent:"center", background:T.lCanvas, padding:12, overflow:"auto" }}>
              {currentScreen ? (
                <div style={{ boxShadow:"0 4px 24px rgba(0,0,0,0.1)", borderRadius:T.rmd, overflow:"hidden", maxWidth:"100%" }}>
                  <ScreenWF dark={dark} viewport={viewport} screen={currentScreen} slim={false} />
                </div>
              ) : (
                <div style={{ padding:40, textAlign:"center" }}>
                  <div style={{ fontSize:32, marginBottom:8 }}>🎨</div>
                  <div style={{ fontSize:14, fontWeight:500, color:T.lText, marginBottom:4 }}>Select a step</div>
                  <div style={{ fontSize:10, color:"#667781" }}>Choose any step from the sidebar to preview its wireframe</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* IA view */}
        {section === "ia" && (
          <div style={{ display:"flex", flexDirection:"column", gap:16, padding:20 }}>
            {IA_GROUPS.map(group => (
              <div key={group.id}>
                <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:8 }}>
                  <span style={{ fontSize:14 }}>{group.icon}</span>
                  <span style={{ fontSize:13, fontWeight:600, color:th.text }}>{group.label}</span>
                  <span style={{ fontSize:9, color:th.textMuted }}>({group.screens.length} screens)</span>
                </div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
                  {group.screens.map(screen => (
                    <div key={screen.id} style={{ padding:"6px 10px", background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rsm, fontSize:9, cursor:"default", display:"flex", alignItems:"center", gap:4 }}>
                      <MockBadge label={screen.tag} type={screen.tag==="core"?"active":screen.tag==="state"?"pending":screen.tag==="form"?"active":screen.tag==="modal"?"pending":"muted"} />
                      <span style={{ color:th.text }}>{screen.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Routes view */}
        {section === "routes" && (
          <div style={{ padding:20 }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Route Table</div>
            <div style={{ overflow:"auto" }}>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:9 }}>
                <thead>
                  <tr style={{ borderBottom:`1px solid ${th.borderMd}` }}>
                    {["Path","Component","Shell","Auth","Note"].map(h => (
                      <th key={h} style={{ textAlign:"left", padding:"6px 10px", color:th.textSec, fontWeight:600 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ROUTES.map((r,i) => (
                    <tr key={i} style={{ borderBottom:`1px solid ${th.border}` }}>
                      <td style={{ padding:"6px 10px", color:T.brand, fontWeight:500 }}>{r.path}</td>
                      <td style={{ padding:"6px 10px", color:th.text }}>{r.comp}</td>
                      <td style={{ padding:"6px 10px", color:th.textSec }}>{r.shell}</td>
                      <td style={{ padding:"6px 10px" }}><MockBadge label={r.auth} type={r.auth==="public"?"muted":"active"} /></td>
                      <td style={{ padding:"6px 10px", color:th.textMuted, maxWidth:200 }}>{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Folders view */}
        {section === "folders" && (
          <div style={{ padding:20 }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>File Structure</div>
            <div style={{ fontFamily:"'JetBrains Mono', monospace", fontSize:10, color:th.textSec, lineHeight:1.8 }}>
              <div>frontend/src/pages/ZiBuzz/</div>
              <div style={{ paddingLeft:16 }}>shared/</div>
              <div style={{ paddingLeft:32 }}>zibuzzIA.js</div>
              <div style={{ paddingLeft:32 }}>zibuzzFlows.js</div>
              <div style={{ paddingLeft:32 }}>zibuzzRoutes.js</div>
              <div style={{ paddingLeft:32 }}>screenRegistry.js</div>
              <div style={{ paddingLeft:16 }}>ZiBuzzUXWizard.js</div>
              <div style={{ paddingLeft:16 }}>index.js</div>
            </div>
          </div>
        )}

        {/* Shells view */}
        {section === "shells" && (
          <div style={{ display:"flex", gap:16, padding:20, flexWrap:"wrap" }}>
            {SHELLS.map(shell => (
              <div key={shell.id} style={{ width:280, background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16 }}>
                <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:4 }}>{shell.label}</div>
                <div style={{ fontSize:9, color:th.textMuted, marginBottom:8 }}>{shell.who}</div>
                <div style={{ fontSize:9, color:th.textSec, lineHeight:1.5 }}>
                  {shell.topbar !== "None" && <div><strong>Topbar:</strong> {shell.topbar}</div>}
                  {shell.sidebar !== "None" && <div><strong>Sidebar:</strong> {shell.sidebar}</div>}
                  <div style={{ marginTop:4 }}>{shell.note}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Tech Stack view */}
        {section === "tech-stack" && (
          <div style={{ padding:20 }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Tech Stack</div>
            <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
              {TECH_STACK.map(([label, value],i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"8px 12px", background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rsm }}>
                  <span style={{ fontSize:10, fontWeight:600, color:th.text, width:100, flexShrink:0 }}>{label}</span>
                  <span style={{ fontSize:10, color:th.textSec }}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Configs view */}
        {section === "configs" && (
          <div style={{ padding:20 }}>
            <div style={{ fontSize:13, fontWeight:600, color:th.text, marginBottom:12 }}>Subscription Tiers</div>
            <div style={{ display:"flex", gap:12, flexWrap:"wrap" }}>
              {CONFIGS.map((cfg,i) => (
                <div key={i} style={{ width:240, background:th.surface, border:`1px solid ${th.border}`, borderRadius:T.rmd, padding:16 }}>
                  <div style={{ fontSize:12, fontWeight:600, color:th.text, marginBottom:8 }}>{cfg.label}</div>
                  {Object.entries(cfg).filter(([k]) => !["name","label"].includes(k)).map(([k,v]) => (
                    <div key={k} style={{ display:"flex", justifyContent:"space-between", padding:"3px 0", fontSize:9 }}>
                      <span style={{ color:th.textMuted }}>{k}</span>
                      <span style={{ color:th.text, fontWeight:500 }}>{String(v)}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}
      </WizardChrome>
    </InspectProvider>
  );
}
